import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  Star, MapPin, MessageCircle, Calendar, Clock, TrendingUp, ShieldCheck,
  BadgeCheck, Wallet, CreditCard, ChevronRight, ChevronLeft, Camera, ArrowLeft,
  ImageIcon, Sparkles, ArrowUpRight, CheckCircle2, AlertCircle, Users,
  Zap, Award, Bell, HelpCircle, Search, LogOut, Home, Briefcase, X,
  Plus, MapPinned, Receipt, Loader2, XCircle, Check, Info,
  ChevronDown, Gift, Eye,
} from "lucide-react";
import { supabase } from "../lib/supabase";
import { useAuth } from "../context/AuthContext";
import { useBreakpoint } from "../hooks/useBreakpoint";
import AppLogo from "../components/AppLogo";
import PortfolioManager from "./PortfolioManager";
import VideoManager from "./VideoManager";

/* ═══════════════════════════════════════════════════════════
   SCHEMA THIS FILE ASSUMES (confirmed via Phase 0 / 0b SQL):
     pro_profiles: jirani_name, pro_number, phone_locked_until,
       availability ('available'|'busy'|'unavailable'), vacation_mode,
       payout_method, payout_mpesa_number, payout_bank_name,
       payout_account_number, payout_account_name
     profiles: avatar_url, pending_avatar_url, avatar_status
     pro_services: name, description, price, duration_minutes, category,
       status ('pending'|'approved'|'rejected'), rejection_reason, is_active,
       headline, included_items, excluded_items, prep_notes, team_size,
       supplies_included (last six added for the service product page —
       see ServiceModal below)
     service_packages: id, service_id, name, price, description, sort_order
       (optional tiers for a service; a service with no rows here just
       shows its single base price, same as before)
     portfolio_images: url, caption, sort_order, is_main, status
     pro_service_areas: area
     pro_availability: day_of_week, start_time, end_time, is_open
     phone_change_requests: reason, reason_other, status, requested_at
     notifications: type, subtype, title, body, link_to, read, status,
       recipient_id, user_id

   ⚠️ The Messages dropdown assumes conversation_participants /
   chat_messages / message_status columns as described in the
   phase1_support_migration.sql RPCs. If your actual chat schema differs,
   the dropdown fails gracefully (shows "Couldn't load messages") rather
   than crashing the dashboard — tell me the real columns and I'll patch
   just that piece.
═══════════════════════════════════════════════════════════ */

/* ─────────────────────────────────────
   FONT INJECTION
───────────────────────────────────── */
if (!document.getElementById("__pp_fonts")) {
  const l = document.createElement("link");
  l.id  = "__pp_fonts";
  l.rel = "stylesheet";
  l.href =
    "https://fonts.googleapis.com/css2?family=Instrument+Serif:ital@0;1&family=DM+Sans:opsz,wght@9..40,300;9..40,400;9..40,500;9..40,600;9..40,700&display=swap";
  document.head.appendChild(l);
}

/* ─────────────────────────────────────
   TOKENS
───────────────────────────────────── */
const T = {
  green:       "#1e9254",
  greenDark:   "#155c36",
  greenDeep:   "#0d2e1a",
  greenTint:   "#eef8f3",
  greenBd:     "#a7d9bc",

  bg:          "#ffffff",
  white:       "#ffffff",
  card2:       "#f7f8fa",
  border:      "#e4e7ef",
  borderFaint: "#eef0f4",
  hoverBg:     "#F3F4F6",

  text:        "#0f1117",
  textMid:     "#2d3549",
  sub:         "#4a5268",
  faint:       "#8b93a7",
  ghost:       "#c7cddb",

  amber:       "#92400e",
  amberTint:   "#fefaf1",
  amberBd:     "#f3d99a",

  red:         "#b3261e",
  redTint:     "#fdf3f2",
  redBd:       "#f0c9c5",

  blue:        "#1d4ed8",
  blueTint:    "#eef3fd",
  blueBd:      "#c7d7f7",

  gold:        "#b8862f",
  goldTint:    "#fbf5e9",
  goldBd:      "#e9d5a3",

  serif: "'Instrument Serif', Georgia, serif",
  sans:  "'DM Sans', -apple-system, BlinkMacSystemFont, sans-serif",

  r8:"8px", r10:"10px", r12:"12px", r14:"14px", r16:"16px", r20:"20px", rFull:"9999px",

  s1: "0 1px 3px rgba(13,46,26,0.05)",
  s2: "0 4px 16px rgba(13,46,26,0.07)",
  s3: "0 10px 34px rgba(13,46,26,0.14)",
  sGreen: "0 6px 20px rgba(30,146,84,0.24)",

  tx: "all 0.18s cubic-bezier(.4,0,.2,1)",
};

/* ─────────────────────────────────────
   GLOBAL CSS
───────────────────────────────────── */
const CSS = `
  *, *::before, *::after { box-sizing: border-box; }
  html { scroll-behavior: smooth; }
  body { margin:0; background:${T.bg}; font-family:${T.sans}; color:${T.text}; -webkit-font-smoothing:antialiased; }
  button:focus-visible, a:focus-visible { outline: 2px solid ${T.green}; outline-offset: 2px; }
  ::-webkit-scrollbar { width:5px; height:5px; }
  ::-webkit-scrollbar-track { background:transparent; }
  ::-webkit-scrollbar-thumb { background:#D7DBE2; border-radius:99px; }
  .jp-scroll::-webkit-scrollbar { display:none; }
  .jp-scroll { scrollbar-width:none; -ms-overflow-style:none; }
  .jss-scroll::-webkit-scrollbar { width: 4px; }
  .jss-scroll::-webkit-scrollbar-track { background: transparent; }
  .jss-scroll::-webkit-scrollbar-thumb { background: ${T.border}; border-radius: 4px; }

  @keyframes pdPulse   { 0%,100%{opacity:1;} 50%{opacity:0.42;} }
  @keyframes fadeUp    { from{opacity:0;transform:translateY(8px);} to{opacity:1;transform:translateY(0);} }
  @keyframes fadeIn    { from{opacity:0;} to{opacity:1;} }
  @keyframes shimmer   { 0%{transform:translateX(-120%);} 100%{transform:translateX(260%);} }
  @keyframes jpFloat   { 0%,100% { transform: translateY(0px); } 50% { transform: translateY(-5px); } }
  @keyframes jpPopIn   { from { opacity:0; transform: translateY(-6px) scale(0.98); } to { opacity:1; transform: translateY(0) scale(1); } }
  @keyframes jpModalIn { from { opacity:0; transform: translateY(10px); } to { opacity:1; transform: translateY(0); } }
  @media (prefers-reduced-motion: reduce) {
    *, *::before, *::after { animation:none !important; transition:none !important; }
  }

  .pd-card {
    background:${T.white}; border:1.5px solid ${T.border}; border-radius:${T.r16};
    box-shadow:${T.s1}; transition:${T.tx};
  }
  .pd-job-card {
    background:${T.white}; border:1.5px solid ${T.border}; border-radius:${T.r12};
    padding:14px 16px; display:flex; align-items:center; gap:14px; cursor:pointer;
    transition:${T.tx};
  }
  .pd-job-card:hover { border-color:${T.greenBd}; box-shadow:${T.s2}; transform:translateY(-1px); }

  .pd-peer-card {
    flex-shrink:0; width:152px; scroll-snap-align:start; cursor:pointer;
    background:${T.white}; border:1px solid ${T.border}; border-radius:${T.r12}; overflow:hidden;
    transition:${T.tx};
  }
  .pd-peer-card:hover { border-color:${T.greenBd}; box-shadow:${T.s2}; transform:translateY(-2px); }

  .pd-sugg-card { border-radius:${T.r12}; padding:14px 16px; transition:${T.tx}; }
  .pd-sugg-card:hover { transform:translateY(-1px); box-shadow:${T.s1}; }

  .pd-chart-toggle {
    padding:6px 15px; border-radius:${T.rFull}; font-size:12px; font-weight:600;
    cursor:pointer; border:1.5px solid ${T.border}; background:${T.white}; color:${T.faint};
    font-family:${T.sans}; transition:${T.tx};
  }
  .pd-chart-toggle:hover { border-color:${T.greenBd}; color:${T.green}; }
  .pd-chart-toggle.active { background:${T.green}; color:#fff; border-color:${T.green}; }

  .pd-stat-tile { border-radius:${T.r14}; padding:14px 15px; transition:${T.tx}; }
  .pd-stat-tile:hover { transform:translateY(-2px); box-shadow:${T.s2}; }

  .pd-prog-fill {
    height:100%; border-radius:${T.rFull};
    background: linear-gradient(90deg, ${T.green}, #34b874);
    transition:width 1s cubic-bezier(0.22,1,0.36,1);
    position:relative; overflow:hidden;
  }
  .pd-prog-fill::after {
    content:""; position:absolute; top:0; left:0; height:100%; width:36%;
    background:linear-gradient(90deg,transparent,rgba(255,255,255,0.55),transparent);
    animation:shimmer 2.4s ease-in-out 1.1s infinite;
  }

  .pd-activity-item { display:flex; align-items:flex-start; gap:12px; position:relative; animation: fadeUp 0.35s ease both; }
  .pd-btn { transition:${T.tx}; }
  .pd-icon-badge:hover { transform: translateY(-1px); box-shadow: ${T.s2}; }
  .pd-method-card:hover { border-color:${T.greenBd} !important; }
  .pd-promo-banner:hover { transform:translateY(-2px); box-shadow:0 14px 38px rgba(13,46,26,0.32); }
  .pd-nav-item:hover { background: ${T.white} !important; }
  .pd-tab { transition:${T.tx}; }
  .pd-dropdown-item:hover { background: ${T.card2} !important; }
  .pd-avail-option:hover { background: ${T.card2} !important; }
  .pd-modal-close:hover { background: ${T.card2} !important; }
`;

const I = {
  mpesa: <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M12 2v20M17 5H9.5a3.5 3.5 0 1 0 0 7h5a3.5 3.5 0 1 1 0 7H6"/></svg>,
  card:  <CreditCard size={14} strokeWidth={1.8} />,
  brief: <ImageIcon size={16} strokeWidth={1.6} />,
};

/* ═══════════════════════════════════════════════════════════
   SHARED PRIMITIVES
═══════════════════════════════════════════════════════════ */
const PALETTE = ["#155c36", "#1e9254", "#0f2e1a", "#374151", "#92400e", "#1d4ed8", "#7c3aed", "#0e7490"];
const avatarColor = (n="") => PALETTE[((n.charCodeAt(0)||0)+(n.charCodeAt(1)||0)) % PALETTE.length];
const toInitials  = (n="") => n.split(" ").map(w=>w[0]).join("").slice(0,2).toUpperCase();

function Avatar({ name="", url, size=44, ring=false }) {
  const [err, setErr] = useState(false);
  useEffect(() => setErr(false), [url]);
  const bg = avatarColor(name);
  return (
    <div style={{
      width:size, height:size, borderRadius:"50%", flexShrink:0, overflow:"hidden",
      background:bg, display:"flex", alignItems:"center", justifyContent:"center",
      fontSize:Math.round(size*0.34), fontWeight:600, color:"#fff", fontFamily:T.sans,
      boxShadow: ring ? `0 0 0 3px #fff, 0 0 0 4.5px ${T.greenBd}` : "none",
    }}>
      {url && !err
        ? <img src={url} alt={name} onError={()=>setErr(true)} style={{width:"100%",height:"100%",objectFit:"cover"}}/>
        : <span style={{userSelect:"none"}}>{toInitials(name)}</span>}
    </div>
  );
}

function Badge({ children, variant="neutral", size="md" }) {
  const map = {
    neutral: { bg:T.card2,     color:T.sub,   bd:T.border  },
    green:   { bg:T.greenTint, color:T.green, bd:T.greenBd },
    amber:   { bg:T.amberTint, color:T.amber, bd:T.amberBd },
    blue:    { bg:T.blueTint,  color:T.blue,  bd:T.blueBd  },
    red:     { bg:T.redTint,   color:T.red,   bd:T.redBd   },
    gold:    { bg:T.goldTint,  color:T.gold,  bd:T.goldBd  },
    dark:    { bg:T.text,      color:"#fff",  bd:T.text    },
  };
  const s = map[variant]||map.neutral;
  const pad = size === "sm" ? "2px 8px" : "3px 10px";
  const fs  = size === "sm" ? 10.5 : 11.5;
  return (
    <span style={{
      display:"inline-flex",alignItems:"center",gap:4,
      background:s.bg,color:s.color,border:`1px solid ${s.bd}`,
      borderRadius:T.rFull,padding:pad,
      fontSize:fs,fontWeight:700,fontFamily:T.sans,whiteSpace:"nowrap",
    }}>{children}</span>
  );
}

function Btn({ children, v="ghost", size="md", onClick, disabled=false, full=false, icon, type="button" }) {
  const [hov,setHov] = useState(false);
  const sz = { sm:{fs:12.5,h:34,pad:"0 16px"}, md:{fs:13,h:40,pad:"0 20px"}, lg:{fs:14,h:46,pad:"0 24px"} }[size];
  const vs = {
    primary:{ bg:hov?T.greenDark:T.green, col:"#fff",   bd:"1px solid transparent", sh:hov?T.sGreen:"0 2px 10px rgba(30,146,84,0.2)" },
    ghost:  { bg:hov?T.hoverBg:T.white,   col:T.textMid, bd:`1.5px solid ${T.border}`, sh:"none" },
    outline:{ bg:hov?T.greenTint:T.white, col:T.green,  bd:`1.5px solid ${T.greenBd}`, sh:"none" },
    dark:   { bg:hov?"#000":T.text,       col:"#fff",   bd:"1px solid transparent", sh:"none" },
  };
  const s = vs[v]||vs.ghost;
  return (
    <button type={type} className="pd-btn" onClick={onClick} disabled={disabled}
      onMouseEnter={()=>setHov(true)} onMouseLeave={()=>setHov(false)}
      style={{
        display:"inline-flex",alignItems:"center",justifyContent:"center",gap:7,
        height:sz.h,padding:sz.pad,borderRadius:T.rFull,
        fontSize:sz.fs,fontWeight:700,fontFamily:T.sans,
        background:s.bg,color:s.col,border:s.bd,boxShadow:s.sh,
        cursor:disabled?"default":"pointer",opacity:disabled?0.45:1,
        whiteSpace:"nowrap",outline:"none",minHeight:44,
        ...(full?{width:"100%"}:{}),
      }}>{icon}{children}</button>
  );
}

function EditorialHeading({ eyebrow, title, sub, size="md" }) {
  const { isMobile } = useBreakpoint();
  const sizes = { sm: isMobile?16:18, md: isMobile?19:22 };
  return (
    <div style={{ marginBottom: sub ? 10 : 8 }}>
      {eyebrow && (
        <p style={{ margin:"0 0 3px", fontSize:10, fontWeight:700, color:T.green,
          textTransform:"uppercase", letterSpacing:"0.09em", fontFamily:T.sans }}>{eyebrow}</p>
      )}
      <h2 style={{ margin:0, fontFamily:T.serif, fontStyle:"italic", fontWeight:400,
        fontSize: sizes[size], color:T.text, letterSpacing:"-0.02em", lineHeight:1.15 }}>{title}</h2>
      {sub && <p style={{ margin:"3px 0 0", fontSize:12.5, color:T.sub, fontFamily:T.sans }}>{sub}</p>}
    </div>
  );
}

function Section({ eyebrow, title, sub, right, children }) {
  const { isMobile } = useBreakpoint();
  return (
    <div className="pd-card" style={{ padding: isMobile ? "18px 16px" : "22px 24px" }}>
      <div style={{ display:"flex", alignItems:"flex-start", justifyContent:"space-between", gap:12, flexWrap: isMobile ? "wrap" : "nowrap" }}>
        <EditorialHeading eyebrow={eyebrow} title={title} sub={sub} size="sm" />
        {right}
      </div>
      <div style={{ marginTop:14 }}>{children}</div>
    </div>
  );
}

function Div({ my=0 }) { return <div style={{height:1,background:T.borderFaint,margin:`${my}px 0`}}/>; }

function VerifyRow({ label, ok }) {
  return (
    <div style={{display:"flex",alignItems:"center",gap:9}}>
      <span style={{
        width:16, height:16, borderRadius:"50%", flexShrink:0,
        background: ok ? T.greenTint : T.card2,
        display:"flex", alignItems:"center", justifyContent:"center",
        color: ok ? T.green : T.ghost, border:`1px solid ${ok ? T.greenBd : T.border}`,
      }}>
        {ok
          ? <svg width="8" height="8" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round"><path d="M20 6L9 17l-5-5"/></svg>
          : <span style={{ width:4, height:4, borderRadius:"50%", background:"currentColor" }} />}
      </span>
      <span style={{fontSize:12.5,color:ok?T.textMid:T.faint,fontFamily:T.sans,fontWeight:ok?600:400}}>{label}</span>
    </div>
  );
}

function SkillPill({ label }) {
  const [hov,setHov] = useState(false);
  return (
    <span onMouseEnter={()=>setHov(true)} onMouseLeave={()=>setHov(false)} style={{
      fontSize:11.5,color:hov?T.textMid:T.sub,
      border:`1px solid ${hov?T.greenBd:T.border}`,
      background:hov?T.greenTint:T.white,
      borderRadius:T.rFull,padding:"5px 12px",
      fontFamily:T.sans,cursor:"default",transition:T.tx,
    }}>{label}</span>
  );
}

function CompletionRing({ pct, size=64 }) {
  const stroke = 5.5;
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const offset = c - (pct/100) * c;
  const color = pct >= 80 ? T.green : pct >= 50 ? T.amber : T.red;
  return (
    <div style={{ position:"relative", width:size, height:size, flexShrink:0 }}>
      <svg width={size} height={size} style={{ transform:"rotate(-90deg)" }}>
        <circle cx={size/2} cy={size/2} r={r} fill="none" stroke={T.borderFaint} strokeWidth={stroke} />
        <circle cx={size/2} cy={size/2} r={r} fill="none" stroke={color} strokeWidth={stroke}
          strokeDasharray={c} strokeDashoffset={offset} strokeLinecap="round"
          style={{ transition:"stroke-dashoffset 1s cubic-bezier(.22,1,.36,1)" }} />
      </svg>
      <div style={{ position:"absolute", inset:0, display:"flex", alignItems:"center", justifyContent:"center", flexDirection:"column" }}>
        <span style={{ fontSize:size*0.26, fontWeight:800, color:T.text, fontFamily:T.sans, letterSpacing:"-0.03em", lineHeight:1 }}>{pct}%</span>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────
   TIME / FORMAT HELPERS
───────────────────────────────────── */
function timeAgo(iso) {
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff/60000);
  if (mins < 1) return "Just now";
  if (mins < 60) return `${mins} min ago`;
  const h = Math.floor(mins/60);
  if (h < 24) return `${h} hour${h>1?"s":""} ago`;
  const d = Math.floor(h/24);
  if (d === 1) return "Yesterday";
  if (d < 7) return `${d} days ago`;
  return new Date(iso).toLocaleDateString("en-KE",{day:"numeric",month:"short"});
}
function fmtKsh(n) { return `KSh ${Math.round(n||0).toLocaleString()}`; }
function fmtShort(iso) { try { return new Date(iso).toLocaleDateString("en-KE",{day:"numeric",month:"short"}); } catch { return ""; } }
function fmtLong(iso) { try { return new Date(iso).toLocaleDateString("en-KE",{day:"numeric",month:"long",year:"numeric"}); } catch { return ""; } }
function fmtDay(iso)   { try { return new Date(iso).toLocaleDateString("en-KE",{weekday:"short"}); } catch { return ""; } }
function fmtClock(iso) { try { return new Date(iso).toLocaleTimeString("en-KE",{hour:"numeric",minute:"2-digit"}); } catch { return ""; } }
function fmtHM(t) { if (!t) return ""; const [h,m] = t.split(":").map(Number); const hh = h>12?h-12:(h||12); return `${hh}:${String(m).padStart(2,"0")} ${h>=12?"PM":"AM"}`; }

function getAvailDot(status) {
  if (status === "available") return T.green;
  if (status === "busy") return T.amber;
  return T.ghost;
}

const WEEKDAYS = ["Sun","Mon","Tue","Wed","Thu","Fri","Sat"];
const DAY_NAMES = ["Sunday","Monday","Tuesday","Wednesday","Thursday","Friday","Saturday"];
const DAY_ORDER = [1,2,3,4,5,6,0];

/* ─────────────────────────────────────
   EARNINGS SERIES BUILDER
───────────────────────────────────── */
function buildSeries(paidJobs, range) {
  const now = new Date();
  const points = [];
  const count = 12;
  for (let i = count - 1; i >= 0; i--) {
    let start, end, label;
    if (range === "Day") {
      const d = new Date(now); d.setHours(0,0,0,0); d.setDate(d.getDate() - i);
      start = new Date(d); end = new Date(d); end.setDate(end.getDate()+1);
      label = d.toLocaleDateString("en-KE",{day:"numeric",month:"short"});
    } else if (range === "Week") {
      const d = new Date(now); d.setHours(0,0,0,0); d.setDate(d.getDate() - d.getDay() - i*7);
      start = new Date(d); end = new Date(d); end.setDate(end.getDate()+7);
      label = start.toLocaleDateString("en-KE",{day:"numeric",month:"short"});
    } else {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      start = d; end = new Date(now.getFullYear(), now.getMonth() - i + 1, 1);
      label = d.toLocaleDateString("en-KE",{month:"short"});
    }
    const total = paidJobs
      .filter(j => { const t = new Date(j.scheduled_time || j.created_at); return t >= start && t < end; })
      .reduce((sum,j) => sum + (j.estimated_price||0), 0);
    points.push({ label, total });
  }
  return points;
}

function EarningsChart({ paidJobs }) {
  const [range, setRange] = useState("Week");
  const [tooltip, setTooltip] = useState(null);
  const svgRef = useRef(null);

  const series = useMemo(() => buildSeries(paidJobs, range), [paidJobs, range]);
  const data   = series.map(p => p.total);
  const labels = series.map(p => p.label);

  const W = 600, H = 168;
  const pad = { t:16, r:12, b:32, l:50 };
  const chartW = W - pad.l - pad.r, chartH = H - pad.t - pad.b;
  const maxV = Math.max(...data, 1) * 1.1;
  const minV = 0;
  const xScale = (i) => pad.l + (i/(data.length-1||1)) * chartW;
  const yScale = (v) => pad.t + chartH - ((v-minV)/(maxV-minV||1)) * chartH;
  const points = data.map((v,i) => [xScale(i), yScale(v)]);
  const linePath = points.reduce((acc,[x,y],i) => {
    if (i===0) return `M ${x},${y}`;
    const [px,py] = points[i-1];
    const cpx = (px+x)/2;
    return acc + ` C ${cpx},${py} ${cpx},${y} ${x},${y}`;
  }, "");
  const areaPath = points.length ? linePath + ` L ${points[points.length-1][0]},${H-pad.b} L ${points[0][0]},${H-pad.b} Z` : "";

  const handleMouseMove = useCallback((e) => {
    const rect = svgRef.current?.getBoundingClientRect();
    if (!rect || !points.length) return;
    const mx = (e.clientX - rect.left) * (W/rect.width);
    let closest = 0, minDist = Infinity;
    points.forEach(([x],i) => { const d = Math.abs(mx-x); if (d<minDist) { minDist=d; closest=i; } });
    setTooltip({ x:points[closest][0], y:points[closest][1], v:data[closest], label:labels[closest] });
  }, [points, data, labels]);

  return (
    <div>
      <div style={{ display:"flex", gap:6, marginBottom:16 }}>
        {["Day","Week","Month"].map(r => (
          <button key={r} className={`pd-chart-toggle${range===r?" active":""}`} onClick={()=>setRange(r)}>{r}</button>
        ))}
      </div>
      {data.every(v=>v===0) ? (
        <div style={{ height:H, display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", gap:6 }}>
          <TrendingUp size={22} color={T.ghost} strokeWidth={1.5} />
          <span style={{ color:T.faint, fontSize:12.5, fontFamily:T.sans }}>No paid bookings in this period yet.</span>
        </div>
      ) : (
        <div style={{ position:"relative" }}>
          <svg ref={svgRef} viewBox={`0 0 ${W} ${H}`} style={{ width:"100%", height:H, overflow:"visible", cursor:"crosshair" }}
            onMouseMove={handleMouseMove} onMouseLeave={()=>setTooltip(null)}>
            <defs>
              <linearGradient id="pdChartGrad" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%"  stopColor={T.green} stopOpacity="0.24"/>
                <stop offset="100%" stopColor={T.green} stopOpacity="0"/>
              </linearGradient>
            </defs>
            {[0,0.25,0.5,0.75,1].map((pct,i) => {
              const y = pad.t + chartH*pct;
              const val = maxV - pct*(maxV-minV);
              return (
                <g key={i}>
                  <line x1={pad.l} y1={y} x2={W-pad.r} y2={y} stroke={T.borderFaint} strokeWidth="1" />
                  <text x={pad.l-8} y={y+4} textAnchor="end" fontSize="10" fill={T.faint} fontFamily={T.sans}>{val>=1000?`${(val/1000).toFixed(0)}k`:val.toFixed(0)}</text>
                </g>
              );
            })}
            <path d={areaPath} fill="url(#pdChartGrad)"/>
            <path d={linePath} fill="none" stroke={T.green} strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"/>
            {points.map(([x],i) => (
              i % Math.ceil(labels.length/6) === 0 && (
                <text key={i} x={x} y={H-6} textAnchor="middle" fontSize="10" fill={T.faint} fontFamily={T.sans}>{labels[i]}</text>
              )
            ))}
            {tooltip && (
              <g>
                <line x1={tooltip.x} y1={pad.t} x2={tooltip.x} y2={H-pad.b} stroke={T.green} strokeWidth="1.5" strokeDasharray="4 3" opacity="0.5"/>
                <circle cx={tooltip.x} cy={tooltip.y} r="5" fill={T.green} stroke="#fff" strokeWidth="2.5"/>
              </g>
            )}
          </svg>
          {tooltip && (
            <div style={{
              position:"absolute", left:Math.min(tooltip.x/W*100,85)+"%", top:Math.max(0,(tooltip.y-16)/H*100)+"%",
              transform:"translate(-50%,-100%)", background:T.text, color:"#fff",
              borderRadius:8, padding:"6px 10px", boxShadow:T.s3, pointerEvents:"none", whiteSpace:"nowrap", zIndex:10,
            }}>
              <p style={{ fontSize:11, color:"rgba(255,255,255,0.6)", margin:"0 0 1px", fontFamily:T.sans }}>{tooltip.label}</p>
              <p style={{ fontSize:13, fontWeight:700, color:"#7fe3ac", margin:0, fontFamily:T.sans }}>{fmtKsh(tooltip.v)}</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function Countdown({ targetIso, dark=true }) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => { const t = setInterval(()=>setNow(Date.now()), 1000); return () => clearInterval(t); }, []);
  const target = new Date(targetIso).getTime();
  const secs = Math.max(0, Math.floor((target - now)/1000));
  const h = Math.floor(secs/3600), m = Math.floor((secs%3600)/60), s = secs%60;
  const pad2 = (n) => String(n).padStart(2,"0");
  const urgent = secs < 600;
  const numCol = dark ? "#fff" : T.text;
  const lblCol = dark ? "rgba(255,255,255,0.55)" : T.faint;

  return (
    <div style={{ display:"flex", alignItems:"flex-end", gap:0 }}>
      {[[h,"HRS"],[m,"MIN"],[s,"SEC"]].map(([val,lbl],i) => (
        <div key={lbl} style={{ display:"flex", alignItems:"flex-end" }}>
          <div style={{ display:"flex", flexDirection:"column", alignItems:"center" }}>
            <span style={{ fontSize:38, fontWeight:800, lineHeight:0.9, letterSpacing:"-0.03em",
              fontFamily:T.sans, color: urgent ? "#ff8a80" : numCol,
              animation: urgent ? "pdPulse 1s infinite" : "none", display:"block" }}>{pad2(val)}</span>
            <span style={{ fontSize:9, fontWeight:700, letterSpacing:"0.1em", marginTop:4,
              fontFamily:T.sans, color: urgent ? "#ff8a80" : lblCol, textTransform:"uppercase" }}>{lbl}</span>
          </div>
          {i<2 && (
            <span style={{ fontSize:26, fontWeight:300, lineHeight:0.9,
              color: lblCol, fontFamily:T.sans, margin:"0 4px 12px" }}>:</span>
          )}
        </div>
      ))}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════
   MODAL SHELL — used by Add Service, Areas & Availability,
   Bookings/Invoice. Consistent header, backdrop, close button.
═══════════════════════════════════════════════════════════ */
function Modal({ open, onClose, title, eyebrow, width=520, children }) {
  if (!open) return null;
  return (
    <div onClick={onClose} style={{
      position:"fixed", inset:0, zIndex:800, background:"rgba(13,46,26,0.5)",
      display:"flex", alignItems:"center", justifyContent:"center", padding:16,
      animation:"fadeIn 0.15s ease-out",
    }}>
      <div onClick={e=>e.stopPropagation()} style={{
        background:T.white, borderRadius:T.r16, width:"100%", maxWidth:width,
        maxHeight:"88vh", overflowY:"auto", boxShadow:T.s3, animation:"jpModalIn 0.22s cubic-bezier(.22,1,.36,1)",
      }} className="jss-scroll">
        <div style={{ padding:"18px 22px", borderBottom:`1px solid ${T.borderFaint}`, display:"flex", alignItems:"center", justifyContent:"space-between", position:"sticky", top:0, background:T.white, zIndex:2 }}>
          <div>
            {eyebrow && <p style={{ margin:"0 0 2px", fontSize:10, fontWeight:700, color:T.green, textTransform:"uppercase", letterSpacing:"0.09em", fontFamily:T.sans }}>{eyebrow}</p>}
            <span style={{ fontSize:18, fontWeight:400, fontStyle:"italic", color:T.text, fontFamily:T.serif }}>{title}</span>
          </div>
          <button onClick={onClose} className="pd-modal-close" style={{ width:32, height:32, borderRadius:"50%", background:"none", border:"none", cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center", color:T.sub, transition:T.tx }}>
            <X size={16} strokeWidth={2}/>
          </button>
        </div>
        <div style={{ padding:22 }}>{children}</div>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════
   ADD A SERVICE MODAL — creates a pending pro_services row,
   shows a live preview of the card as it'll appear once
   approved, and reflects "Pending review" clearly.

   This is also where the pro fills in everything the service
   product page (ServiceProductPage.jsx) can show: a headline,
   what's included/excluded, prep notes, team size, whether
   supplies are included, media, and optional price tiers
   (service_packages). Every one of these is optional — if the
   pro leaves it blank, the product page just omits that
   section rather than showing something fake.
═══════════════════════════════════════════════════════════ */
const SERVICE_CATEGORIES = [
  "House Cleaning","Deep Cleaning","Plumbing","Electrical","TV & Electronics",
  "Hair & Beauty","Tutoring","Photography","Gardening","Carpentry",
  "Moving","Childcare","Fitness","Auto Repair","Errands","Personal Chef","Other",
];

/* Small reusable "tag list" input — used for both included_items and
   excluded_items. Type a line, hit Enter or "Add", it becomes a chip;
   click the chip's X to remove. Keeps the array of plain strings the
   product page expects, no fabricated defaults. */
function TagListInput({ items, onChange, placeholder, chipVariant = "neutral" }) {
  const [draft, setDraft] = useState("");
  const rowStyles = {
    neutral: { fg: T.textMid, dot: T.faint },
    green:   { fg: T.text,    dot: T.green },
  }[chipVariant];

  const add = () => {
    const val = draft.trim();
    if (!val) return;
    onChange([...items, val]);
    setDraft("");
  };
  const remove = (i) => onChange(items.filter((_, idx) => idx !== i));
  const moveUp = (i) => { if (i===0) return; const next=[...items]; [next[i-1],next[i]]=[next[i],next[i-1]]; onChange(next); };
  const moveDown = (i) => { if (i===items.length-1) return; const next=[...items]; [next[i+1],next[i]]=[next[i],next[i+1]]; onChange(next); };

  // Rendered as a real vertical bullet list — one line per point — so it
  // reads the same way it will on the service card, not as a paragraph.
  return (
    <div>
      {items.length > 0 && (
        <div style={{ display:"flex", flexDirection:"column", marginBottom:10, border:`1.5px solid ${T.border}`, borderRadius:T.r10, overflow:"hidden" }}>
          {items.map((it, i) => (
            <div key={i} style={{
              display:"flex", alignItems:"center", gap:9, padding:"8px 10px",
              borderBottom: i<items.length-1 ? `1px solid ${T.borderFaint}` : "none", background:T.white,
            }}>
              <span style={{ width:6, height:6, borderRadius:"50%", background:rowStyles.dot, flexShrink:0 }} />
              <span style={{ flex:1, fontSize:12.5, color:rowStyles.fg, fontFamily:T.sans, lineHeight:1.4 }}>{it}</span>
              <div style={{ display:"flex", gap:2, flexShrink:0 }}>
                <button onClick={()=>moveUp(i)} disabled={i===0} title="Move up" style={{ width:20, height:20, borderRadius:5, border:"none", background:"none", cursor:i===0?"default":"pointer", color:i===0?T.borderFaint:T.faint, display:"flex", alignItems:"center", justifyContent:"center" }}>
                  <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4"><path d="M18 15l-6-6-6 6"/></svg>
                </button>
                <button onClick={()=>moveDown(i)} disabled={i===items.length-1} title="Move down" style={{ width:20, height:20, borderRadius:5, border:"none", background:"none", cursor:i===items.length-1?"default":"pointer", color:i===items.length-1?T.borderFaint:T.faint, display:"flex", alignItems:"center", justifyContent:"center" }}>
                  <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4"><path d="M6 9l6 6 6-6"/></svg>
                </button>
                <button onClick={()=>remove(i)} title="Remove" style={{ width:20, height:20, borderRadius:5, border:"none", background:"none", cursor:"pointer", color:T.red, display:"flex", alignItems:"center", justifyContent:"center" }}>
                  <X size={11} strokeWidth={2.2}/>
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
      <div style={{ display:"flex", gap:8 }}>
        <input
          value={draft}
          onChange={e=>setDraft(e.target.value)}
          onKeyDown={e=>{ if (e.key==="Enter") { e.preventDefault(); add(); } }}
          placeholder={placeholder}
          style={{ flex:1, padding:"9px 12px", borderRadius:T.r10, border:`1.5px solid ${T.border}`, fontSize:13, fontFamily:T.sans, outline:"none" }}
        />
        <Btn v="outline" size="sm" onClick={add}>Add point</Btn>
      </div>
    </div>
  );
}

/* Price tiers (service_packages) — edit-only, since it needs the real
   service id. If the pro never adds a tier, the product page just uses
   the single base price above; this is purely additive. */
function PackagesEditor({ serviceId, onToast }) {
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [draft, setDraft] = useState({ name: "", price: "", description: "" });
  const [adding, setAdding] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase.from("service_packages").select("id, name, price, description, sort_order").eq("service_id", serviceId).order("sort_order", { ascending:true });
      if (error) throw error;
      setPackages(data || []);
    } catch {
      setPackages([]); // table may not exist in every environment yet — fail soft
    } finally { setLoading(false); }
  }, [serviceId]);

  useEffect(() => { load(); }, [load]);

  const addPackage = async () => {
    if (!draft.name.trim() || !(Number(draft.price) > 0)) { onToast?.("Give the tier a name and a price above 0.", "error"); return; }
    setAdding(true);
    try {
      const { data, error } = await supabase.from("service_packages").insert({
        service_id: serviceId, name: draft.name.trim(), price: Number(draft.price),
        description: draft.description.trim() || null, sort_order: packages.length,
      }).select().single();
      if (error) throw error;
      setPackages(p => [...p, data]);
      setDraft({ name:"", price:"", description:"" });
    } catch (err) {
      onToast?.(err.message || "Couldn't add that tier — service_packages may not be set up yet.", "error");
    } finally { setAdding(false); }
  };

  const removePackage = async (pkg) => {
    setPackages(p => p.filter(x => x.id !== pkg.id));
    try { await supabase.from("service_packages").delete().eq("id", pkg.id); } catch { load(); }
  };

  return (
    <div>
      <label style={{ fontSize:11, fontWeight:700, color:T.sub, textTransform:"uppercase", letterSpacing:"0.06em", fontFamily:T.sans, display:"block", marginBottom:6 }}>
        Price tiers (optional)
      </label>
      <p style={{ fontSize:11.5, color:T.faint, fontFamily:T.sans, margin:"0 0 10px", lineHeight:1.5 }}>
        Add tiers like "Basic", "Deep Clean", "Move-out" if you charge different prices for different scopes. Leave this empty to just use the single price above.
      </p>

      {loading ? (
        <p style={{ fontSize:12.5, color:T.faint, fontFamily:T.sans }}>Loading tiers…</p>
      ) : (
        <>
          {packages.length > 0 && (
            <div style={{ display:"flex", flexDirection:"column", gap:6, marginBottom:12 }}>
              {packages.map(pkg => (
                <div key={pkg.id} style={{ display:"flex", alignItems:"center", justifyContent:"space-between", gap:10, padding:"9px 12px", border:`1.5px solid ${T.border}`, borderRadius:T.r10 }}>
                  <div style={{ minWidth:0 }}>
                    <p style={{ margin:0, fontSize:12.5, fontWeight:700, color:T.text, fontFamily:T.sans }}>{pkg.name}</p>
                    {pkg.description && <p style={{ margin:"1px 0 0", fontSize:11, color:T.faint, fontFamily:T.sans, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{pkg.description}</p>}
                  </div>
                  <div style={{ display:"flex", alignItems:"center", gap:10, flexShrink:0 }}>
                    <span style={{ fontSize:13, fontWeight:700, color:T.text, fontFamily:T.sans }}>{fmtKsh(pkg.price)}</span>
                    <button onClick={()=>removePackage(pkg)} style={{ width:22, height:22, borderRadius:"50%", border:"none", background:T.card2, color:T.red, cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center" }}><X size={11} strokeWidth={2.2}/></button>
                  </div>
                </div>
              ))}
            </div>
          )}

          <div style={{ display:"grid", gridTemplateColumns:"1.2fr 0.8fr", gap:8, marginBottom:8 }}>
            <input value={draft.name} onChange={e=>setDraft(d=>({...d,name:e.target.value}))} placeholder="Tier name, e.g. Deep Clean" style={{ padding:"9px 12px", borderRadius:T.r10, border:`1.5px solid ${T.border}`, fontSize:13, fontFamily:T.sans, outline:"none" }} />
            <input value={draft.price} onChange={e=>setDraft(d=>({...d,price:e.target.value}))} type="number" placeholder="Price (KES)" style={{ padding:"9px 12px", borderRadius:T.r10, border:`1.5px solid ${T.border}`, fontSize:13, fontFamily:T.sans, outline:"none" }} />
          </div>
          <input value={draft.description} onChange={e=>setDraft(d=>({...d,description:e.target.value}))} placeholder="Short description (optional)" style={{ width:"100%", padding:"9px 12px", borderRadius:T.r10, border:`1.5px solid ${T.border}`, fontSize:13, fontFamily:T.sans, outline:"none", boxSizing:"border-box", marginBottom:8 }} />
          <Btn v="outline" size="sm" onClick={addPackage} disabled={adding} icon={<Plus size={13} strokeWidth={2.4}/>}>{adding ? "Adding…" : "Add tier"}</Btn>
        </>
      )}
    </div>
  );
}

/* service_media is unconfirmed against your live schema — this block
   fails open (try/catch, same pattern as the Messages dropdown) so an
   unrecognized table just means the media grid stays empty rather than
   crashing the modal. Confirm the table and I'll drop this note. */
const MAX_GALLERY_PHOTOS = 5;

function ServiceModal({ open, onClose, proId, defaultCategory, editingService, onSaved, onToast }) {
  // A brand-new service has no id until the first save, so media/tiers
  // can't attach to it yet. Rather than closing the modal after that
  // first save (which was hiding the media step from new pros entirely),
  // we hold the freshly-created row here and switch into edit mode in
  // place — the same modal, now with the media/tiers sections visible.
  const [createdService, setCreatedService] = useState(null);
  const activeService = createdService || editingService;
  const isEdit = !!activeService;
  const [name, setName] = useState("");
  const [headline, setHeadline] = useState("");
  const [description, setDescription] = useState("");
  const [price, setPrice] = useState("");
  const [duration, setDuration] = useState("");
  const [category, setCategory] = useState(defaultCategory || SERVICE_CATEGORIES[0]);
  const [includedItems, setIncludedItems] = useState([]);
  const [excludedItems, setExcludedItems] = useState([]);
  const [prepNotes, setPrepNotes] = useState("");
  const [teamSize, setTeamSize] = useState("");
  const [suppliesIncluded, setSuppliesIncluded] = useState(null); // null = not set, true/false = set
  const [media, setMedia] = useState([]);
  const [pendingFiles, setPendingFiles] = useState([]); // staged photos/video picked before the service has an id yet
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);
  const fileRef = useRef(null);

  const loadMedia = useCallback(async (serviceId) => {
    try {
      const { data } = await supabase.from("service_media").select("id, url, type, sort_order, is_cover, stage, pair_group").eq("service_id", serviceId).order("sort_order", { ascending:true });
      setMedia(data || []);
    } catch { setMedia([]); }
  }, []);

  useEffect(() => {
    if (!open) { return; }
    setCreatedService(null); // fresh open — don't carry over a previous session's just-created row
    setPendingFiles(p => { p.forEach(f=>URL.revokeObjectURL(f.previewUrl)); return []; });
    if (editingService) {
      setName(editingService.name || "");
      setHeadline(editingService.headline || "");
      setDescription(editingService.description || "");
      setPrice(String(editingService.price ?? ""));
      setDuration(editingService.duration_minutes ? String(editingService.duration_minutes) : "");
      setCategory(editingService.category || defaultCategory || SERVICE_CATEGORIES[0]);
      setIncludedItems(editingService.included_items || []);
      setExcludedItems(editingService.excluded_items || []);
      setPrepNotes(editingService.prep_notes || "");
      setTeamSize(editingService.team_size ? String(editingService.team_size) : "");
      setSuppliesIncluded(editingService.supplies_included ?? null);
      loadMedia(editingService.id);
    } else {
      setName(""); setHeadline(""); setDescription(""); setPrice(""); setDuration("");
      setCategory(defaultCategory || SERVICE_CATEGORIES[0]); setMedia([]);
      setIncludedItems([]); setExcludedItems([]); setPrepNotes(""); setTeamSize(""); setSuppliesIncluded(null);
    }
    setError(null);
  }, [open, editingService, defaultCategory, loadMedia]);

  const valid = name.trim().length > 1 && Number(price) > 0;

  const galleryCount = isEdit ? media.filter(m=>!m.stage).length : pendingFiles.length;

  // Before the service has an id, we can't upload to storage yet — so we
  // just hold the picked files locally (with a blob preview URL) and show
  // them in the same grid. persistPendingFiles turns them into real
  // service_media rows right after the service is created.
  const stageFiles = (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (!files.length) return;
    const room = MAX_GALLERY_PHOTOS - pendingFiles.length;
    if (room <= 0) { onToast?.(`You can add up to ${MAX_GALLERY_PHOTOS} photos/videos to this service.`, "error"); return; }
    const toAdd = files.slice(0, room).map(f => ({
      localId: `local_${Date.now()}_${Math.random().toString(36).slice(2)}`,
      file: f, previewUrl: URL.createObjectURL(f), type: f.type.startsWith("video/") ? "video" : "image",
    }));
    if (toAdd.length < files.length) onToast?.(`Only added ${toAdd.length} — that's the ${MAX_GALLERY_PHOTOS}-photo limit for this service.`, "error");
    setPendingFiles(p => [...p, ...toAdd]);
  };
  const unstageFile = (localId) => {
    setPendingFiles(p => { const hit = p.find(f=>f.localId===localId); if (hit) URL.revokeObjectURL(hit.previewUrl); return p.filter(f=>f.localId!==localId); });
  };
  const pickGalleryFile = (e) => { isEdit ? uploadMedia(e) : stageFiles(e); };

  const persistPendingFiles = async (serviceId) => {
    if (!pendingFiles.length) return [];
    const uploaded = [];
    for (const [i, staged] of pendingFiles.entries()) {
      try {
        const path = `service_${serviceId}/${Date.now()}-${staged.file.name}`;
        const { data, error } = await supabase.storage.from("service-media").upload(path, staged.file, { upsert:true });
        if (error) throw error;
        const { data: { publicUrl } } = supabase.storage.from("service-media").getPublicUrl(data.path);
        const { data: row, error: insErr } = await supabase.from("service_media").insert({
          service_id: serviceId, url: publicUrl, type: staged.type, sort_order: i, is_cover: i === 0, stage: null,
        }).select().single();
        if (insErr) throw insErr;
        uploaded.push(row);
      } catch (err) {
        onToast?.(`Couldn't upload ${staged.file.name} — you can add it below instead.`, "error");
      }
      URL.revokeObjectURL(staged.previewUrl);
    }
    return uploaded;
  };

  const uploadMedia = async (e, stage = null) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (!files.length || !isEdit) return;
    if (!stage && galleryCount >= MAX_GALLERY_PHOTOS) {
      onToast?.(`You can add up to ${MAX_GALLERY_PHOTOS} photos/videos to this service.`, "error");
      return;
    }
    const room = stage ? files.length : Math.min(files.length, MAX_GALLERY_PHOTOS - galleryCount);
    const toUpload = files.slice(0, room);
    if (!stage && toUpload.length < files.length) {
      onToast?.(`Only added ${toUpload.length} — that's the ${MAX_GALLERY_PHOTOS}-photo limit for this service.`, "error");
    }
    setUploading(true);
    try {
      for (const file of toUpload) {
        const isVideo = file.type.startsWith("video/");
        if (isVideo && file.size > 40*1024*1024) { onToast?.(`${file.name} is over 40MB, skipped.`, "error"); continue; }
        if (!isVideo && file.size > 8*1024*1024) { onToast?.(`${file.name} is over 8MB, skipped.`, "error"); continue; }
        const path = `service_${activeService.id}/${Date.now()}-${file.name}`;
        const { data, error } = await supabase.storage.from("service-media").upload(path, file, { upsert:true });
        if (error) throw error;
        const { data: { publicUrl } } = supabase.storage.from("service-media").getPublicUrl(data.path);
        const { data: row, error: insErr } = await supabase.from("service_media").insert({
          service_id: activeService.id, url: publicUrl, type: isVideo?"video":"image",
          sort_order: media.length, is_cover: media.filter(m=>!m.stage).length === 0 && !stage,
          stage: stage || null,
        }).select().single();
        if (insErr) throw insErr;
        setMedia(m => [...m, row]);
      }
      if (toUpload.length) onToast?.("Media added.", "success");
    } catch (err) {
      onToast?.(err.message || "Couldn't upload that file — the service_media table may not exist yet.", "error");
    } finally { setUploading(false); }
  };

  const moveStagedFile = (index, dir) => {
    const to = index + dir;
    if (to < 0 || to >= pendingFiles.length) return;
    setPendingFiles(p => { const next=[...p]; [next[index],next[to]]=[next[to],next[index]]; return next; });
  };

  const moveGalleryItem = (index, dir) => {
    const gallery = media.filter(m=>!m.stage);
    const staged = media.filter(m=>m.stage);
    const to = index + dir;
    if (to < 0 || to >= gallery.length) return;
    const next = [...gallery];
    [next[index], next[to]] = [next[to], next[index]];
    const withMeta = next.map((m,i) => ({ ...m, sort_order:i, is_cover: i===0 }));
    setMedia([...withMeta, ...staged]);
    (async () => {
      try {
        await Promise.all(withMeta.map(m =>
          supabase.from("service_media").update({ sort_order:m.sort_order, is_cover:m.is_cover }).eq("id", m.id)
        ));
      } catch { onToast?.("Order changed here, but didn't save — try again.", "error"); }
    })();
  };

  const removeMedia = async (item) => {
    setMedia(m => m.filter(x => x.id !== item.id));
    try { await supabase.from("service_media").delete().eq("id", item.id); } catch { loadMedia(activeService.id); }
  };

  const handleSubmit = async () => {
    if (!valid) { setError("Give it a name and a price above 0."); return; }
    setSaving(true); setError(null);
    try {
      const sharedPatch = {
        name: name.trim(),
        headline: headline.trim() || null,
        description: description.trim() || null,
        price: Number(price),
        duration_minutes: duration ? Number(duration) : null,
        category,
        included_items: includedItems.length ? includedItems : null,
        excluded_items: excludedItems.length ? excludedItems : null,
        prep_notes: prepNotes.trim() || null,
        team_size: teamSize ? Number(teamSize) : null,
        supplies_included: suppliesIncluded,
      };
      if (isEdit) {
        const { error: updErr } = await supabase.from("pro_services").update(sharedPatch).eq("id", activeService.id);
        if (updErr) throw updErr;
        const merged = { ...activeService, ...sharedPatch };
        onSaved(merged);
        if (createdService) setCreatedService(merged);
        onToast?.("Service updated.", "success");
        // Only auto-close when editing an existing, already-saved service.
        // A first-time create stays open below so photos/video/tiers can
        // be added right away instead of the pro having to reopen it.
        if (!createdService && editingService) onClose();
      } else {
        const { data, error: insErr } = await supabase.from("pro_services").insert({
          pro_id: proId, ...sharedPatch,
        }).select().single();
        if (insErr) throw insErr;

        try {
          await supabase.from("notifications").insert({
            type: "pro_review", subtype: "service_pending",
            title: "New service awaiting approval",
            body: `"${name.trim()}" (${fmtKsh(Number(price))}) was submitted for review.`,
            link_to: `/admin/services/${data.id}`,
            recipient_id: null, read: false, status: "unread",
          });
        } catch { /* best-effort */ }

        onSaved(data);
        setCreatedService(data); // switch this same modal into edit mode
        if (pendingFiles.length) {
          const uploaded = await persistPendingFiles(data.id);
          setMedia(uploaded);
          setPendingFiles([]);
        }
        onToast?.("Service submitted for review.", "success");
      }
    } catch (err) {
      setError(err.message || "Couldn't save that service.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal open={open} onClose={onClose} title={isEdit ? "Edit service" : "Add a service"} width={600}>
      <div style={{ display:"flex", flexDirection:"column", gap:14 }}>
        <div>
          <label style={{ fontSize:11, fontWeight:700, color:T.sub, textTransform:"uppercase", letterSpacing:"0.06em", fontFamily:T.sans, display:"block", marginBottom:6 }}>Service name</label>
          <input value={name} onChange={e=>setName(e.target.value)} placeholder="e.g. Deep kitchen clean" style={{ width:"100%", padding:"11px 13px", borderRadius:T.r10, border:`1.5px solid ${T.border}`, fontSize:14, fontFamily:T.sans, outline:"none", boxSizing:"border-box" }} />
        </div>
        <div>
          <label style={{ fontSize:11, fontWeight:700, color:T.sub, textTransform:"uppercase", letterSpacing:"0.06em", fontFamily:T.sans, display:"block", marginBottom:6 }}>Headline <span style={{fontWeight:400,textTransform:"none",letterSpacing:0,color:T.faint}}>(optional — shown instead of the name at the top of your listing)</span></label>
          <input value={headline} onChange={e=>setHeadline(e.target.value)} placeholder="e.g. Premium Deep House Cleaning Service" style={{ width:"100%", padding:"11px 13px", borderRadius:T.r10, border:`1.5px solid ${T.border}`, fontSize:14, fontFamily:T.sans, outline:"none", boxSizing:"border-box" }} />
        </div>
        <div>
          <label style={{ fontSize:11, fontWeight:700, color:T.sub, textTransform:"uppercase", letterSpacing:"0.06em", fontFamily:T.sans, display:"block", marginBottom:6 }}>Description</label>
          <textarea value={description} onChange={e=>setDescription(e.target.value)} rows={3} placeholder="What's included, what to expect" style={{ width:"100%", padding:"11px 13px", borderRadius:T.r10, border:`1.5px solid ${T.border}`, fontSize:14, fontFamily:T.sans, outline:"none", resize:"vertical", boxSizing:"border-box" }} />
        </div>
        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12 }}>
          <div>
            <label style={{ fontSize:11, fontWeight:700, color:T.sub, textTransform:"uppercase", letterSpacing:"0.06em", fontFamily:T.sans, display:"block", marginBottom:6 }}>Price (KES)</label>
            <input value={price} onChange={e=>setPrice(e.target.value)} type="number" placeholder="e.g. 2000" style={{ width:"100%", padding:"11px 13px", borderRadius:T.r10, border:`1.5px solid ${T.border}`, fontSize:14, fontFamily:T.sans, outline:"none", boxSizing:"border-box" }} />
          </div>
          <div>
            <label style={{ fontSize:11, fontWeight:700, color:T.sub, textTransform:"uppercase", letterSpacing:"0.06em", fontFamily:T.sans, display:"block", marginBottom:6 }}>Duration (min)</label>
            <input value={duration} onChange={e=>setDuration(e.target.value)} type="number" placeholder="e.g. 90" style={{ width:"100%", padding:"11px 13px", borderRadius:T.r10, border:`1.5px solid ${T.border}`, fontSize:14, fontFamily:T.sans, outline:"none", boxSizing:"border-box" }} />
          </div>
        </div>
        <div>
          <label style={{ fontSize:11, fontWeight:700, color:T.sub, textTransform:"uppercase", letterSpacing:"0.06em", fontFamily:T.sans, display:"block", marginBottom:6 }}>Category</label>
          <select value={category} onChange={e=>setCategory(e.target.value)} style={{ width:"100%", padding:"11px 13px", borderRadius:T.r10, border:`1.5px solid ${T.border}`, fontSize:14, fontFamily:T.sans, outline:"none", boxSizing:"border-box" }}>
            {SERVICE_CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>

        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12 }}>
          <div>
            <label style={{ fontSize:11, fontWeight:700, color:T.sub, textTransform:"uppercase", letterSpacing:"0.06em", fontFamily:T.sans, display:"block", marginBottom:6 }}>Team size <span style={{fontWeight:400,textTransform:"none",letterSpacing:0,color:T.faint}}>(optional)</span></label>
            <input value={teamSize} onChange={e=>setTeamSize(e.target.value)} type="number" min="1" placeholder="e.g. 1" style={{ width:"100%", padding:"11px 13px", borderRadius:T.r10, border:`1.5px solid ${T.border}`, fontSize:14, fontFamily:T.sans, outline:"none", boxSizing:"border-box" }} />
          </div>
          <div>
            <label style={{ fontSize:11, fontWeight:700, color:T.sub, textTransform:"uppercase", letterSpacing:"0.06em", fontFamily:T.sans, display:"block", marginBottom:6 }}>Supplies <span style={{fontWeight:400,textTransform:"none",letterSpacing:0,color:T.faint}}>(optional)</span></label>
            <div style={{ display:"flex", gap:8 }}>
              {[["Not set", null],["Included", true],["Client provides", false]].map(([lbl,val]) => (
                <button key={lbl} onClick={()=>setSuppliesIncluded(val)} type="button" style={{
                  flex:1, padding:"10px 6px", borderRadius:T.r10, cursor:"pointer", fontFamily:T.sans, fontSize:11.5, fontWeight:700,
                  border:`1.5px solid ${suppliesIncluded===val?T.green:T.border}`, background: suppliesIncluded===val?T.greenTint:T.white, color: suppliesIncluded===val?T.green:T.sub,
                }}>{lbl}</button>
              ))}
            </div>
          </div>
        </div>

        <div>
          <label style={{ fontSize:11, fontWeight:700, color:T.sub, textTransform:"uppercase", letterSpacing:"0.06em", fontFamily:T.sans, display:"block", marginBottom:6 }}>What's included <span style={{fontWeight:400,textTransform:"none",letterSpacing:0,color:T.faint}}>(optional)</span></label>
          <TagListInput items={includedItems} onChange={setIncludedItems} placeholder="e.g. Kitchen cleaning — press Enter to add" chipVariant="green" />
        </div>
        <div>
          <label style={{ fontSize:11, fontWeight:700, color:T.sub, textTransform:"uppercase", letterSpacing:"0.06em", fontFamily:T.sans, display:"block", marginBottom:6 }}>What's not included <span style={{fontWeight:400,textTransform:"none",letterSpacing:0,color:T.faint}}>(optional)</span></label>
          <TagListInput items={excludedItems} onChange={setExcludedItems} placeholder="e.g. Pest control — press Enter to add" chipVariant="neutral" />
        </div>
        <div>
          <label style={{ fontSize:11, fontWeight:700, color:T.sub, textTransform:"uppercase", letterSpacing:"0.06em", fontFamily:T.sans, display:"block", marginBottom:6 }}>How clients should prepare <span style={{fontWeight:400,textTransform:"none",letterSpacing:0,color:T.faint}}>(optional)</span></label>
          <textarea value={prepNotes} onChange={e=>setPrepNotes(e.target.value)} rows={2} placeholder="e.g. Please ensure access to water and electricity, secure valuables…" style={{ width:"100%", padding:"11px 13px", borderRadius:T.r10, border:`1.5px solid ${T.border}`, fontSize:14, fontFamily:T.sans, outline:"none", resize:"vertical", boxSizing:"border-box" }} />
        </div>

        {/* Photos & video — you can start adding these right now, even
            before saving. Anything you pick here uploads for real the
            moment you hit Save/Submit below. */}
        <div>
          <div style={{ display:"flex", alignItems:"baseline", justifyContent:"space-between", marginBottom:6 }}>
            <label style={{ fontSize:11, fontWeight:700, color:T.sub, textTransform:"uppercase", letterSpacing:"0.06em", fontFamily:T.sans }}>
              Photos & video — this is your product card, customers see it in search
            </label>
            <span style={{ fontSize:11, color: galleryCount>=MAX_GALLERY_PHOTOS ? T.amber : T.faint, fontFamily:T.sans, fontWeight:600, flexShrink:0 }}>{galleryCount}/{MAX_GALLERY_PHOTOS}</span>
          </div>
          <div style={{ display:"grid", gridTemplateColumns:"repeat(5,1fr)", gap:8 }}>
            {isEdit ? media.filter(m=>!m.stage).map((m,i,arr) => (
              <div key={m.id} style={{ position:"relative", borderRadius:T.r10, overflow:"hidden", aspectRatio:"1/1", border:`1.5px solid ${i===0?T.green:T.border}` }}>
                {m.type === "video" ? <video src={m.url} style={{width:"100%",height:"100%",objectFit:"cover"}} muted/> : <img src={m.url} alt="" style={{width:"100%",height:"100%",objectFit:"cover"}}/>}
                {i===0 && <div style={{position:"absolute",top:4,left:4}}><Badge variant="green" size="sm">Main photo</Badge></div>}
                <button onClick={()=>removeMedia(m)} style={{ position:"absolute", top:4, right:4, width:18, height:18, borderRadius:"50%", border:"none", background:"rgba(13,17,23,0.55)", color:"#fff", cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center" }}><X size={10} strokeWidth={2.4}/></button>
                <div style={{ position:"absolute", bottom:0, left:0, right:0, display:"flex", padding:4, gap:3, background:"linear-gradient(to top, rgba(0,0,0,0.6), transparent)" }}>
                  <button onClick={()=>moveGalleryItem(i,-1)} disabled={i===0} title="Move earlier" style={{ flex:1, height:20, borderRadius:5, border:"none", background:"rgba(255,255,255,0.92)", cursor:i===0?"default":"pointer", opacity:i===0?0.4:1, display:"flex", alignItems:"center", justifyContent:"center" }}><ChevronLeft size={12} strokeWidth={2.4}/></button>
                  <button onClick={()=>moveGalleryItem(i,1)} disabled={i===arr.length-1} title="Move later" style={{ flex:1, height:20, borderRadius:5, border:"none", background:"rgba(255,255,255,0.92)", cursor:i===arr.length-1?"default":"pointer", opacity:i===arr.length-1?0.4:1, display:"flex", alignItems:"center", justifyContent:"center" }}><ChevronRight size={12} strokeWidth={2.4}/></button>
                </div>
              </div>
            )) : pendingFiles.map((f,i,arr) => (
              <div key={f.localId} style={{ position:"relative", borderRadius:T.r10, overflow:"hidden", aspectRatio:"1/1", border:`1.5px solid ${i===0?T.green:T.border}` }}>
                {f.type === "video" ? <video src={f.previewUrl} style={{width:"100%",height:"100%",objectFit:"cover"}} muted/> : <img src={f.previewUrl} alt="" style={{width:"100%",height:"100%",objectFit:"cover"}}/>}
                {i===0 && <div style={{position:"absolute",top:4,left:4}}><Badge variant="green" size="sm">Main photo</Badge></div>}
                <button onClick={()=>unstageFile(f.localId)} style={{ position:"absolute", top:4, right:4, width:18, height:18, borderRadius:"50%", border:"none", background:"rgba(13,17,23,0.55)", color:"#fff", cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center" }}><X size={10} strokeWidth={2.4}/></button>
                <div style={{ position:"absolute", bottom:0, left:0, right:0, display:"flex", padding:4, gap:3, background:"linear-gradient(to top, rgba(0,0,0,0.6), transparent)" }}>
                  <button onClick={()=>moveStagedFile(i,-1)} disabled={i===0} title="Move earlier" style={{ flex:1, height:20, borderRadius:5, border:"none", background:"rgba(255,255,255,0.92)", cursor:i===0?"default":"pointer", opacity:i===0?0.4:1, display:"flex", alignItems:"center", justifyContent:"center" }}><ChevronLeft size={12} strokeWidth={2.4}/></button>
                  <button onClick={()=>moveStagedFile(i,1)} disabled={i===arr.length-1} title="Move later" style={{ flex:1, height:20, borderRadius:5, border:"none", background:"rgba(255,255,255,0.92)", cursor:i===arr.length-1?"default":"pointer", opacity:i===arr.length-1?0.4:1, display:"flex", alignItems:"center", justifyContent:"center" }}><ChevronRight size={12} strokeWidth={2.4}/></button>
                </div>
              </div>
            ))}
            {galleryCount < MAX_GALLERY_PHOTOS && (
              <button onClick={()=>fileRef.current?.click()} disabled={uploading} style={{ aspectRatio:"1/1", borderRadius:T.r10, border:`1.5px dashed ${T.border}`, background:T.card2, cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center", color:T.faint }}>
                <Plus size={16} strokeWidth={2}/>
              </button>
            )}
          </div>
          <input ref={fileRef} type="file" accept="image/*,video/*" multiple style={{display:"none"}} onChange={pickGalleryFile} />
          <p style={{ fontSize:10.5, color:T.faint, fontFamily:T.sans, margin:"8px 0 0" }}>
            Up to {MAX_GALLERY_PHOTOS} photos or videos. Use the arrows on each one to set the order — whichever ends up first is your main photo, the one shown in search. {!isEdit && "These upload for real once you save below."}
          </p>
        </div>

        {/* Price tiers & before/after — need a real service id, which
            exists once the form above has been saved at least once
            (this same modal switches into that state automatically). */}
        {isEdit ? (
          <>
            <Div my={2}/>
            <PackagesEditor serviceId={activeService.id} onToast={onToast} />
            <BeforeAfterUploader serviceId={activeService.id} media={media} setMedia={setMedia} onToast={onToast} />
          </>
        ) : (
          <div style={{ background:T.card2, border:`1.5px dashed ${T.border}`, borderRadius:T.r12, padding:"12px 14px", display:"flex", gap:10, alignItems:"flex-start" }}>
            <Sparkles size={15} color={T.faint} strokeWidth={1.8} style={{flexShrink:0, marginTop:1}}/>
            <p style={{ margin:0, fontSize:11.5, color:T.sub, fontFamily:T.sans, lineHeight:1.6 }}>
              Price tiers and before/after photo pairs become available once you save the basics above.
            </p>
          </div>
        )}

        {/* Live preview */}
        {(name || price) && (
          <div>
            <p style={{ fontSize:10.5, fontWeight:700, color:T.faint, textTransform:"uppercase", letterSpacing:"0.08em", fontFamily:T.sans, margin:"4px 0 8px" }}>Preview — customers won't see this until approved</p>
            <div style={{ border:`1.5px solid ${T.border}`, borderRadius:T.r12, padding:"13px 15px", display:"flex", alignItems:"center", justifyContent:"space-between", gap:10 }}>
              <div style={{ minWidth:0 }}>
                <p style={{ margin:0, fontSize:13.5, fontWeight:700, color:T.text, fontFamily:T.sans, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{headline || name || "Service name"}</p>
                <p style={{ margin:"3px 0 0", fontSize:11.5, color:T.faint, fontFamily:T.sans }}>{category}{duration ? ` · ${duration} min` : ""}</p>
              </div>
              <div style={{ textAlign:"right", flexShrink:0 }}>
                <p style={{ margin:"0 0 5px", fontSize:14, fontWeight:800, color:T.text, fontFamily:T.sans }}>{price ? fmtKsh(Number(price)) : "—"}</p>
                <Badge variant="amber">Pending review</Badge>
              </div>
            </div>
          </div>
        )}

        {error && <div style={{ fontSize:12.5, color:T.red, fontFamily:T.sans }}>{error}</div>}

        <div style={{ display:"flex", gap:8, marginTop:4 }}>
          <Btn v="primary" onClick={handleSubmit} disabled={saving} icon={saving ? <Loader2 size={14} style={{animation:"pdPulse 0.8s linear infinite"}}/> : <Plus size={14}/>}>
            {saving ? "Saving…" : createdService ? "Save changes" : isEdit ? "Save changes" : "Submit for review"}
          </Btn>
          <Btn v="ghost" onClick={onClose}>{isEdit ? "Done" : "Cancel"}</Btn>
        </div>
      </div>
    </Modal>
  );
}

/* Before/after photo pairs — edit-only. A pro picks a "before" photo and
   an "after" photo for the same pair_group; the product page only shows
   a before/after section when a real, complete pair like this exists —
   nothing is auto-paired from ordinary gallery photos. */
function BeforeAfterUploader({ serviceId, media, setMedia, onToast }) {
  const beforeRef = useRef(null);
  const afterRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const pairs = useMemo(() => {
    const groups = {};
    media.forEach(m => {
      if (!m.stage) return;
      const g = m.pair_group || "default";
      groups[g] = groups[g] || { group: g };
      groups[g][m.stage] = m;
    });
    return Object.values(groups);
  }, [media]);

  const openPairGroup = pairs.find(p => !p.before || !p.after)?.group || `pair_${Date.now()}`;

  const upload = async (file, stage) => {
    if (!file) return;
    if (file.size > 8*1024*1024) { onToast?.("Photo must be under 8MB", "error"); return; }
    setUploading(true);
    try {
      const path = `service_${serviceId}/${stage}-${Date.now()}-${file.name}`;
      const { data, error } = await supabase.storage.from("service-media").upload(path, file, { upsert:true });
      if (error) throw error;
      const { data: { publicUrl } } = supabase.storage.from("service-media").getPublicUrl(data.path);
      const { data: row, error: insErr } = await supabase.from("service_media").insert({
        service_id: serviceId, url: publicUrl, type: "image",
        sort_order: media.length, is_cover: false, stage, pair_group: openPairGroup,
      }).select().single();
      if (insErr) throw insErr;
      setMedia(m => [...m, row]);
      onToast?.(`${stage === "before" ? "Before" : "After"} photo added.`, "success");
    } catch (err) {
      onToast?.(err.message || "Couldn't upload that photo.", "error");
    } finally { setUploading(false); }
  };

  const removePair = async (pair) => {
    const ids = [pair.before?.id, pair.after?.id].filter(Boolean);
    setMedia(m => m.filter(x => !ids.includes(x.id)));
    try { await supabase.from("service_media").delete().in("id", ids); } catch { /* best-effort */ }
  };

  return (
    <div>
      <Div my={2}/>
      <label style={{ fontSize:11, fontWeight:700, color:T.sub, textTransform:"uppercase", letterSpacing:"0.06em", fontFamily:T.sans, display:"block", marginBottom:6 }}>
        Before &amp; after photos <span style={{fontWeight:400,textTransform:"none",letterSpacing:0,color:T.faint}}>(optional — real proof of work beats stock photos)</span>
      </label>
      {pairs.filter(p=>p.before||p.after).map(p => (
        <div key={p.group} style={{ display:"grid", gridTemplateColumns:"1fr 1fr auto", gap:8, marginBottom:8, alignItems:"center" }}>
          {["before","after"].map(stage => (
            <div key={stage} style={{ position:"relative", aspectRatio:"1/1", borderRadius:T.r10, overflow:"hidden", border:`1.5px solid ${T.border}`, background:T.card2 }}>
              {p[stage] ? <img src={p[stage].url} alt={stage} style={{width:"100%",height:"100%",objectFit:"cover"}}/> : (
                <div style={{ width:"100%", height:"100%", display:"flex", alignItems:"center", justifyContent:"center" }}>
                  <span style={{ fontSize:10.5, color:T.faint, fontFamily:T.sans, textTransform:"capitalize" }}>Add {stage}</span>
                </div>
              )}
              <span style={{ position:"absolute", top:4, left:4, background: stage==="after"?T.green:"rgba(13,17,23,0.6)", color:"#fff", fontSize:8.5, fontWeight:700, textTransform:"uppercase", borderRadius:T.rFull, padding:"2px 6px", fontFamily:T.sans }}>{stage}</span>
            </div>
          ))}
          <button onClick={()=>removePair(p)} style={{ width:24, height:24, borderRadius:"50%", border:"none", background:T.card2, color:T.red, cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center" }}><X size={12} strokeWidth={2.2}/></button>
        </div>
      ))}
      <div style={{ display:"flex", gap:8 }}>
        <Btn v="outline" size="sm" onClick={()=>beforeRef.current?.click()} disabled={uploading}>Upload "before"</Btn>
        <Btn v="outline" size="sm" onClick={()=>afterRef.current?.click()} disabled={uploading}>Upload "after"</Btn>
      </div>
      <input ref={beforeRef} type="file" accept="image/*" style={{display:"none"}} onChange={e=>{ upload(e.target.files?.[0], "before"); e.target.value=""; }} />
      <input ref={afterRef} type="file" accept="image/*" style={{display:"none"}} onChange={e=>{ upload(e.target.files?.[0], "after"); e.target.value=""; }} />
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════
   SERVICE AREAS & AVAILABILITY MODAL — real read/write to
   pro_service_areas + pro_availability + pro_profiles.availability.
   No admin approval needed for this one (per spec) — admin just sees it.
═══════════════════════════════════════════════════════════ */
function AreasAvailabilityModal({ open, onClose, pro, onToast, onProUpdate }) {
  const [status, setStatus] = useState(pro?.availability || "available");
  const [areas, setAreas] = useState([]);
  const [areaDraft, setAreaDraft] = useState("");
  const [schedule, setSchedule] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    if (!pro?.id) return;
    setLoading(true);
    try {
      const [areasRes, schedRes] = await Promise.all([
        supabase.from("pro_service_areas").select("id, area").eq("pro_id", pro.id),
        supabase.from("pro_availability").select("day_of_week, start_time, end_time, is_open").eq("pro_id", pro.id),
      ]);
      setAreas(areasRes.data || []);
      const byDay = {};
      (schedRes.data || []).forEach(d => { byDay[d.day_of_week] = d; });
      setSchedule(DAY_ORDER.map(dow => byDay[dow] || { day_of_week: dow, is_open: false, start_time: "08:00", end_time: "17:00" }));
    } catch {
      setAreas([]);
      setSchedule(DAY_ORDER.map(dow => ({ day_of_week: dow, is_open: false, start_time: "08:00", end_time: "17:00" })));
    } finally {
      setLoading(false);
    }
  }, [pro?.id]);

  useEffect(() => { if (open) { setStatus(pro?.availability || "available"); load(); } }, [open, load, pro?.availability]);

  const addArea = async () => {
    const val = areaDraft.trim();
    if (!val) return;
    setAreaDraft("");
    const tmpId = `tmp_${Date.now()}`;
    setAreas(a => [...a, { id: tmpId, area: val }]);
    try {
      const { data, error } = await supabase.from("pro_service_areas").insert({ pro_id: pro.id, area: val }).select().single();
      if (error) throw error;
      setAreas(a => a.map(x => x.id === tmpId ? data : x));
    } catch (err) {
      setAreas(a => a.filter(x => x.id !== tmpId));
      onToast?.(err.message || "Couldn't add that area.", "error");
    }
  };
  const removeArea = async (row) => {
    setAreas(a => a.filter(x => x.id !== row.id));
    try { if (!String(row.id).startsWith("tmp_")) await supabase.from("pro_service_areas").delete().eq("id", row.id); }
    catch { load(); }
  };

  const cycleStatus = async () => {
    const order = ["available","busy","unavailable"];
    const next = order[(order.indexOf(status)+1) % order.length];
    const prev = status;
    setStatus(next);
    try {
      const { error } = await supabase.from("pro_profiles").update({ availability: next }).eq("id", pro.id);
      if (error) throw error;
      onProUpdate?.({ availability: next });
    } catch (err) {
      setStatus(prev);
      onToast?.(err.message || "Couldn't update status.", "error");
    }
  };

  const saveSchedule = async () => {
    setSaving(true);
    try {
      const rows = schedule.map(d => ({ pro_id: pro.id, day_of_week: d.day_of_week, is_open: d.is_open, start_time: d.start_time, end_time: d.end_time }));
      const { error } = await supabase.from("pro_availability").upsert(rows, { onConflict: "pro_id,day_of_week" });
      if (error) throw error;
      onToast?.("Availability saved — this shows on your profile immediately.", "success");
      onClose();
    } catch (err) {
      onToast?.(err.message || "Couldn't save your schedule.", "error");
    } finally {
      setSaving(false);
    }
  };

  const statusCfg = {
    available:   { label: "Available now",  dot: T.green },
    busy:        { label: "Busy",           dot: T.amber },
    unavailable: { label: "Unavailable",    dot: T.red   },
  };

  return (
    <Modal open={open} onClose={onClose} eyebrow="Where & when you work" title="Service areas & availability" width={560}>
      {loading ? (
        <div style={{ padding:"30px 0", textAlign:"center", color:T.faint, fontFamily:T.sans, fontSize:13 }}>Loading…</div>
      ) : (
        <div style={{ display:"flex", flexDirection:"column", gap:22 }}>
          <div>
            <p style={{ fontSize:10.5, fontWeight:700, color:T.faint, textTransform:"uppercase", letterSpacing:"0.08em", fontFamily:T.sans, margin:"0 0 10px" }}>Current status</p>
            <button onClick={cycleStatus} className="pd-avail-option" style={{ display:"inline-flex", alignItems:"center", gap:10, background:T.card2, border:`1.5px solid ${T.border}`, borderRadius:T.rFull, padding:"9px 16px", cursor:"pointer", transition:T.tx }}>
              <div style={{ width:8, height:8, borderRadius:"50%", background:statusCfg[status].dot, animation: status==="available" ? "pdPulse 2s infinite" : "none" }} />
              <span style={{ fontSize:14, fontWeight:700, color:T.text, fontFamily:T.sans }}>{statusCfg[status].label}</span>
              <ChevronRight size={14} color={T.faint} strokeWidth={1.75}/>
            </button>
            <p style={{ fontSize:11, color:T.faint, fontFamily:T.sans, margin:"8px 0 0" }}>Tap to cycle. Updates live across the site immediately.</p>
          </div>

          <Div/>

          <div>
            <p style={{ fontSize:10.5, fontWeight:700, color:T.faint, textTransform:"uppercase", letterSpacing:"0.08em", fontFamily:T.sans, margin:"0 0 10px" }}>Service areas</p>
            <div style={{ display:"flex", flexWrap:"wrap", gap:6, marginBottom:10 }}>
              {areas.map(a => (
                <span key={a.id} style={{ display:"inline-flex", alignItems:"center", gap:6, background:T.card2, border:`1px solid ${T.border}`, borderRadius:T.rFull, padding:"5px 6px 5px 12px", fontSize:12.5, fontWeight:600, color:T.textMid, fontFamily:T.sans }}>
                  <MapPin size={10} strokeWidth={2}/> {a.area}
                  <button onClick={()=>removeArea(a)} style={{ background:"none", border:"none", cursor:"pointer", padding:2, display:"flex", color:T.faint }}><X size={11} strokeWidth={2}/></button>
                </span>
              ))}
            </div>
            <div style={{ display:"flex", gap:8 }}>
              <input value={areaDraft} onChange={e=>setAreaDraft(e.target.value)} onKeyDown={e=>{ if (e.key==="Enter") addArea(); }} placeholder="e.g. Nyali" style={{ flex:1, padding:"9px 12px", borderRadius:T.r10, border:`1.5px solid ${T.border}`, fontSize:13, fontFamily:T.sans, outline:"none" }} />
              <Btn v="outline" size="sm" onClick={addArea}>Add</Btn>
            </div>
          </div>

          <Div/>

          <div>
            <p style={{ fontSize:10.5, fontWeight:700, color:T.faint, textTransform:"uppercase", letterSpacing:"0.08em", fontFamily:T.sans, margin:"0 0 10px" }}>Weekly hours</p>
            <div style={{ display:"flex", flexDirection:"column", gap:6 }}>
              {schedule.map(d => (
                <div key={d.day_of_week} style={{ display:"flex", alignItems:"center", gap:10, padding:"6px 0" }}>
                  <label style={{ width:96, display:"flex", alignItems:"center", gap:7, fontSize:12.5, fontWeight:600, color:T.textMid, fontFamily:T.sans, cursor:"pointer" }}>
                    <input type="checkbox" checked={d.is_open} onChange={e=>setSchedule(s=>s.map(x=>x.day_of_week===d.day_of_week?{...x,is_open:e.target.checked}:x))} />
                    {DAY_NAMES[d.day_of_week].slice(0,3)}
                  </label>
                  {d.is_open ? (
                    <div style={{ display:"flex", alignItems:"center", gap:6 }}>
                      <input type="time" value={d.start_time} onChange={e=>setSchedule(s=>s.map(x=>x.day_of_week===d.day_of_week?{...x,start_time:e.target.value}:x))} style={{ padding:"6px 8px", borderRadius:T.r8, border:`1.5px solid ${T.border}`, fontSize:12.5, fontFamily:T.sans }}/>
                      <span style={{ color:T.faint, fontSize:12 }}>–</span>
                      <input type="time" value={d.end_time} onChange={e=>setSchedule(s=>s.map(x=>x.day_of_week===d.day_of_week?{...x,end_time:e.target.value}:x))} style={{ padding:"6px 8px", borderRadius:T.r8, border:`1.5px solid ${T.border}`, fontSize:12.5, fontFamily:T.sans }}/>
                    </div>
                  ) : (
                    <span style={{ fontSize:12.5, color:T.faint, fontFamily:T.sans }}>Closed</span>
                  )}
                </div>
              ))}
            </div>
          </div>

          <div style={{ display:"flex", gap:8 }}>
            <Btn v="primary" onClick={saveSchedule} disabled={saving}>{saving ? "Saving…" : "Save changes"}</Btn>
            <Btn v="ghost" onClick={onClose}>Close</Btn>
          </div>
        </div>
      )}
    </Modal>
  );
}

/* ═══════════════════════════════════════════════════════════
   MEDIA MODAL — thin wrapper around the real, already-working
   PortfolioManager (photos) and VideoManager (videos) components.
   Nothing custom to fetch/upload here; this just gives them a
   tabbed home inside the dashboard instead of onboarding.
═══════════════════════════════════════════════════════════ */
function MediaModal({ open, onClose, proId, defaultCategory, onToast }) {
  const [tab, setTab] = useState("photos");
  return (
    <Modal open={open} onClose={onClose} eyebrow="What customers see" title="Photos & videos" width={640}>
      <div style={{ display:"flex", gap:6, marginBottom:18 }}>
        {[["photos","Photos"],["videos","Videos"]].map(([id,label]) => (
          <button key={id} className={`pd-chart-toggle${tab===id?" active":""}`} onClick={()=>setTab(id)} style={{ padding:"7px 18px" }}>{label}</button>
        ))}
      </div>
      {tab === "photos"
        ? <PortfolioManager proId={proId} />
        : <VideoManager proId={proId} defaultCategory={defaultCategory} onToast={onToast} />}
    </Modal>
  );
}

/* ═══════════════════════════════════════════════════════════
   BIO EDIT MODAL — was routing to onboarding; now a real
   in-dashboard update to pro_profiles.bio / title.
═══════════════════════════════════════════════════════════ */
function BioEditModal({ open, onClose, pro, onToast, onProUpdate }) {
  const [bio, setBio] = useState(pro?.bio || "");
  const [title, setTitle] = useState(pro?.title || "");
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (open) { setBio(pro?.bio || ""); setTitle(pro?.title || ""); } }, [open, pro]);

  const save = async () => {
    setSaving(true);
    try {
      const { error } = await supabase.from("pro_profiles").update({ bio: bio.trim(), title: title.trim() || null }).eq("id", pro.id);
      if (error) throw error;
      onProUpdate({ bio: bio.trim(), title: title.trim() || null });
      onToast?.("Bio updated.", "success");
      onClose();
    } catch (err) {
      onToast?.(err.message || "Couldn't save your bio.", "error");
    } finally { setSaving(false); }
  };

  return (
    <Modal open={open} onClose={onClose} eyebrow="Tell your story" title="Your bio" width={520}>
      <div style={{ display:"flex", flexDirection:"column", gap:14 }}>
        <div>
          <label style={{ fontSize:11, fontWeight:700, color:T.sub, textTransform:"uppercase", letterSpacing:"0.06em", fontFamily:T.sans, display:"block", marginBottom:6 }}>Headline / title</label>
          <input value={title} onChange={e=>setTitle(e.target.value)} placeholder="e.g. Deep-clean specialist, 6 years experience" style={{ width:"100%", padding:"11px 13px", borderRadius:T.r10, border:`1.5px solid ${T.border}`, fontSize:14, fontFamily:T.sans, outline:"none", boxSizing:"border-box" }} />
        </div>
        <div>
          <label style={{ fontSize:11, fontWeight:700, color:T.sub, textTransform:"uppercase", letterSpacing:"0.06em", fontFamily:T.sans, display:"block", marginBottom:6 }}>Bio</label>
          <textarea value={bio} onChange={e=>setBio(e.target.value)} rows={6} placeholder="What makes you good at what you do?" style={{ width:"100%", padding:"11px 13px", borderRadius:T.r10, border:`1.5px solid ${T.border}`, fontSize:14, fontFamily:T.sans, outline:"none", resize:"vertical", boxSizing:"border-box" }} />
          <p style={{ fontSize:11, color: bio.length<80?T.amber:T.faint, fontFamily:T.sans, margin:"6px 0 0" }}>{bio.length} characters{bio.length<80 ? " — aim for 80+" : ""}</p>
        </div>
        <div style={{ display:"flex", gap:8 }}>
          <Btn v="primary" onClick={save} disabled={saving}>{saving?"Saving…":"Save"}</Btn>
          <Btn v="ghost" onClick={onClose}>Cancel</Btn>
        </div>
      </div>
    </Modal>
  );
}

/* ═══════════════════════════════════════════════════════════
   PAYOUT SETUP MODAL — was routing to onboarding; now a real
   in-dashboard update to pro_profiles payout_* columns.
═══════════════════════════════════════════════════════════ */
function PayoutSetupModal({ open, onClose, pro, onToast, onProUpdate }) {
  const [method, setMethod] = useState(pro?.payout_method || "mpesa");
  const [mpesa, setMpesa] = useState(pro?.payout_mpesa_number || "");
  const [bankName, setBankName] = useState(pro?.payout_bank_name || "");
  const [accName, setAccName] = useState(pro?.payout_account_name || "");
  const [accNum, setAccNum] = useState(pro?.payout_account_number || "");
  const [saving, setSaving] = useState(false);

  useEffect(() => { if (open) {
    setMethod(pro?.payout_method || "mpesa");
    setMpesa(pro?.payout_mpesa_number || "");
    setBankName(pro?.payout_bank_name || "");
    setAccName(pro?.payout_account_name || "");
    setAccNum(pro?.payout_account_number || "");
  } }, [open, pro]);

  const valid = method === "mpesa" ? mpesa.trim().length >= 9 : (bankName.trim() && accName.trim() && accNum.trim());

  const save = async () => {
    if (!valid) return;
    setSaving(true);
    try {
      const patch = method === "mpesa"
        ? { payout_method: "mpesa", payout_mpesa_number: mpesa.trim() }
        : { payout_method: "bank", payout_bank_name: bankName.trim(), payout_account_name: accName.trim(), payout_account_number: accNum.trim() };
      const { error } = await supabase.from("pro_profiles").update(patch).eq("id", pro.id);
      if (error) throw error;
      onProUpdate(patch);
      onToast?.("Payout details saved.", "success");
      onClose();
    } catch (err) {
      onToast?.(err.message || "Couldn't save payout details.", "error");
    } finally { setSaving(false); }
  };

  return (
    <Modal open={open} onClose={onClose} eyebrow="Getting paid" title="Payout details" width={480}>
      <div style={{ display:"flex", flexDirection:"column", gap:14 }}>
        <div style={{ display:"flex", gap:8 }}>
          {["mpesa","bank"].map(m => (
            <button key={m} onClick={()=>setMethod(m)} style={{
              flex:1, padding:"10px 0", borderRadius:T.r10, cursor:"pointer", fontFamily:T.sans, fontSize:13, fontWeight:700,
              border:`1.5px solid ${method===m?T.green:T.border}`, background: method===m?T.greenTint:T.white, color: method===m?T.green:T.sub,
            }}>{m==="mpesa" ? "M-Pesa" : "Bank"}</button>
          ))}
        </div>
        {method === "mpesa" ? (
          <div>
            <label style={{ fontSize:11, fontWeight:700, color:T.sub, textTransform:"uppercase", letterSpacing:"0.06em", fontFamily:T.sans, display:"block", marginBottom:6 }}>M-Pesa number</label>
            <input value={mpesa} onChange={e=>setMpesa(e.target.value)} placeholder="07XX XXX XXX" style={{ width:"100%", padding:"11px 13px", borderRadius:T.r10, border:`1.5px solid ${T.border}`, fontSize:14, fontFamily:T.sans, outline:"none", boxSizing:"border-box" }} />
          </div>
        ) : (
          <>
            <div><label style={{fontSize:11,fontWeight:700,color:T.sub,textTransform:"uppercase",letterSpacing:"0.06em",fontFamily:T.sans,display:"block",marginBottom:6}}>Bank name</label>
              <input value={bankName} onChange={e=>setBankName(e.target.value)} style={{ width:"100%", padding:"11px 13px", borderRadius:T.r10, border:`1.5px solid ${T.border}`, fontSize:14, fontFamily:T.sans, outline:"none", boxSizing:"border-box" }} /></div>
            <div><label style={{fontSize:11,fontWeight:700,color:T.sub,textTransform:"uppercase",letterSpacing:"0.06em",fontFamily:T.sans,display:"block",marginBottom:6}}>Account name</label>
              <input value={accName} onChange={e=>setAccName(e.target.value)} style={{ width:"100%", padding:"11px 13px", borderRadius:T.r10, border:`1.5px solid ${T.border}`, fontSize:14, fontFamily:T.sans, outline:"none", boxSizing:"border-box" }} /></div>
            <div><label style={{fontSize:11,fontWeight:700,color:T.sub,textTransform:"uppercase",letterSpacing:"0.06em",fontFamily:T.sans,display:"block",marginBottom:6}}>Account number</label>
              <input value={accNum} onChange={e=>setAccNum(e.target.value)} style={{ width:"100%", padding:"11px 13px", borderRadius:T.r10, border:`1.5px solid ${T.border}`, fontSize:14, fontFamily:T.sans, outline:"none", boxSizing:"border-box" }} /></div>
          </>
        )}
        <div style={{ display:"flex", gap:8 }}>
          <Btn v="primary" onClick={save} disabled={saving || !valid}>{saving?"Saving…":"Save payout details"}</Btn>
          <Btn v="ghost" onClick={onClose}>Cancel</Btn>
        </div>
      </div>
    </Modal>
  );
}

/* ═══════════════════════════════════════════════════════════
   JIRANI NAME MODAL — was routing to onboarding; now a real
   in-dashboard update to pro_profiles.jirani_name.
═══════════════════════════════════════════════════════════ */
function JiraniNameModal({ open, onClose, pro, onToast, onProUpdate }) {
  const [name, setName] = useState(pro?.jirani_name || "");
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (open) setName(pro?.jirani_name || ""); }, [open, pro]);

  const save = async () => {
    if (!name.trim()) return;
    setSaving(true);
    try {
      const { error } = await supabase.from("pro_profiles").update({ jirani_name: name.trim() }).eq("id", pro.id);
      if (error) throw error;
      onProUpdate({ jirani_name: name.trim() });
      onToast?.("Jirani Name saved.", "success");
      onClose();
    } catch (err) {
      onToast?.(err.message || "Couldn't save that name.", "error");
    } finally { setSaving(false); }
  };

  return (
    <Modal open={open} onClose={onClose} eyebrow="How clients know you" title="Your Jirani Name" width={420}>
      <div style={{ display:"flex", flexDirection:"column", gap:14 }}>
        <input value={name} onChange={e=>setName(e.target.value)} placeholder="e.g. Jirani Wanjiru" style={{ width:"100%", padding:"11px 13px", borderRadius:T.r10, border:`1.5px solid ${T.border}`, fontSize:14, fontFamily:T.sans, outline:"none", boxSizing:"border-box" }} />
        <div style={{ display:"flex", gap:8 }}>
          <Btn v="primary" onClick={save} disabled={saving || !name.trim()}>{saving?"Saving…":"Save"}</Btn>
          <Btn v="ghost" onClick={onClose}>Cancel</Btn>
        </div>
      </div>
    </Modal>
  );
}

/* ═══════════════════════════════════════════════════════════
   VERIFICATION MODAL — was routing to onboarding; now a real
   in-dashboard checklist. verification_documents and
   background_check_requests are unconfirmed against your live
   schema, so both actions fail open with a toast rather than
   crash if those tables don't exist yet.
═══════════════════════════════════════════════════════════ */
function VerificationModal({ open, onClose, pro, onToast }) {
  const [requesting, setRequesting] = useState(false);
  const idFileRef = useRef(null);

  const requestBackgroundCheck = async () => {
    setRequesting(true);
    try {
      const { error } = await supabase.from("background_check_requests").insert({ pro_id: pro.id, status: "pending" });
      if (error) throw error;
      try {
        await supabase.from("notifications").insert({
          type: "pro_review", subtype: "background_check_pending",
          title: "Background check requested",
          body: `${pro.jirani_name || pro.full_name} requested a background check.`,
          link_to: `/admin/pros/${pro.id}?tab=verification`,
          recipient_id: null, read: false, status: "unread",
        });
      } catch { /* best-effort */ }
      onToast?.("Background check requested — we'll notify you once it's done.", "success");
    } catch (err) {
      onToast?.(err.message || "Couldn't submit that request — this feature may not be set up yet.", "error");
    } finally { setRequesting(false); }
  };

  const uploadIdDoc = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file || !pro?.id) return;
    if (file.size > 8 * 1024 * 1024) { onToast?.("File must be under 8MB", "error"); return; }
    try {
      const path = `pro_${pro.id}/id-${Date.now()}.${file.name.split(".").pop()}`;
      const { data, error } = await supabase.storage.from("verification_docs").upload(path, file, { upsert: true });
      if (error) throw error;
      const { data: { publicUrl } } = supabase.storage.from("verification_docs").getPublicUrl(data.path);
      const { error: insErr } = await supabase.from("verification_documents").insert({ pro_id: pro.id, url: publicUrl, doc_type: "identity", status: "pending" });
      if (insErr) throw insErr;
      try {
        await supabase.from("notifications").insert({
          type: "pro_review", subtype: "identity_pending",
          title: "Identity document submitted",
          body: `${pro.jirani_name || pro.full_name} submitted an ID for verification.`,
          link_to: `/admin/pros/${pro.id}?tab=verification`,
          recipient_id: null, read: false, status: "unread",
        });
      } catch { /* best-effort */ }
      onToast?.("ID submitted — pending admin review.", "success");
    } catch (err) {
      onToast?.(err.message || "Couldn't upload that document — this feature may not be set up yet.", "error");
    }
  };

  return (
    <Modal open={open} onClose={onClose} eyebrow="Trust & safety" title="Verification status" width={480}>
      <div style={{ display:"flex", flexDirection:"column", gap:16 }}>
        <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", gap:10 }}>
          <VerifyRow label="Identity verified" ok={!!pro.verified}/>
          {!pro.verified && <Btn v="outline" size="sm" onClick={()=>idFileRef.current?.click()}>Upload ID</Btn>}
        </div>
        <input ref={idFileRef} type="file" accept="image/*,.pdf" style={{display:"none"}} onChange={uploadIdDoc} />

        <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", gap:10 }}>
          <VerifyRow label="Phone verified" ok={!!pro.phone_verified}/>
          {!pro.phone_verified && <span style={{ fontSize:11.5, color:T.faint, fontFamily:T.sans }}>Managed in Account settings</span>}
        </div>

        <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", gap:10 }}>
          <VerifyRow label="Background checked" ok={!!pro.background_checked}/>
          {!pro.background_checked && <Btn v="outline" size="sm" onClick={requestBackgroundCheck} disabled={requesting}>{requesting?"Requesting…":"Request check"}</Btn>}
        </div>

        <p style={{ fontSize:11.5, color:T.faint, fontFamily:T.sans, margin:0, lineHeight:1.6 }}>
          Verified pros rank higher in search. Reviews usually take 1–2 business days.
        </p>
      </div>
    </Modal>
  );
}

/* ═══════════════════════════════════════════════════════════
   BOOKINGS + INVOICE MODAL — replaces /bookings nav entirely.
   List view of real bookings; click through to an invoice view
   built to the same visual quality as the rest of the page.
═══════════════════════════════════════════════════════════ */
function InvoiceView({ job, pro, onBack }) {
  const statusCfg = {
    paid:    { label: "Paid",    variant: "green" },
    pending: { label: "Pending", variant: "amber" },
  };
  const paymentBadge = statusCfg[job.payment_status] || statusCfg.pending;
  const subtotal = job.estimated_price || 0;
  const serviceFeePct = 0.1;
  const serviceFee = Math.round(subtotal * serviceFeePct);
  const total = subtotal; // estimated_price is treated as the client-facing total; fee is JiraniApp's cut, not an add-on

  return (
    <div>
      <button onClick={onBack} style={{ display:"flex", alignItems:"center", gap:6, background:"none", border:"none", cursor:"pointer", color:T.sub, fontSize:12.5, fontWeight:600, fontFamily:T.sans, padding:0, marginBottom:16 }}>
        <ArrowLeft size={13} strokeWidth={2}/> Back to bookings
      </button>

      <div style={{ border:`1.5px solid ${T.border}`, borderRadius:T.r14, overflow:"hidden" }}>
        <div style={{ background:`linear-gradient(150deg, ${T.greenDeep} 0%, #0f3a22 60%, ${T.greenDark} 100%)`, padding:"22px 24px" }}>
          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", gap:12 }}>
            <div>
              <p style={{ margin:"0 0 4px", fontSize:10, fontWeight:700, color:"#7fe3ac", textTransform:"uppercase", letterSpacing:"0.1em", fontFamily:T.sans }}>Invoice</p>
              <p style={{ margin:0, fontSize:20, fontWeight:400, fontStyle:"italic", color:"#fff", fontFamily:T.serif }}>{job.description || "Service"}</p>
            </div>
            <Badge variant="dark">#{String(job.id).slice(0,8).toUpperCase()}</Badge>
          </div>
        </div>

        <div style={{ padding:"22px 24px" }}>
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:16, marginBottom:20 }}>
            <div>
              <p style={{ margin:"0 0 4px", fontSize:10.5, fontWeight:700, color:T.faint, textTransform:"uppercase", letterSpacing:"0.07em", fontFamily:T.sans }}>Billed to</p>
              <p style={{ margin:0, fontSize:13.5, fontWeight:700, color:T.text, fontFamily:T.sans }}>{job.customer?.full_name || "Client"}</p>
            </div>
            <div>
              <p style={{ margin:"0 0 4px", fontSize:10.5, fontWeight:700, color:T.faint, textTransform:"uppercase", letterSpacing:"0.07em", fontFamily:T.sans }}>Provided by</p>
              <p style={{ margin:0, fontSize:13.5, fontWeight:700, color:T.text, fontFamily:T.sans }}>{pro?.jirani_name || pro?.full_name}</p>
              <p style={{ margin:"1px 0 0", fontSize:11.5, color:T.faint, fontFamily:T.sans }}>Pro · {pro?.pro_number || "—"}</p>
            </div>
            <div>
              <p style={{ margin:"0 0 4px", fontSize:10.5, fontWeight:700, color:T.faint, textTransform:"uppercase", letterSpacing:"0.07em", fontFamily:T.sans }}>Date</p>
              <p style={{ margin:0, fontSize:13, color:T.textMid, fontFamily:T.sans }}>{fmtLong(job.scheduled_time || job.created_at)}</p>
            </div>
            <div>
              <p style={{ margin:"0 0 4px", fontSize:10.5, fontWeight:700, color:T.faint, textTransform:"uppercase", letterSpacing:"0.07em", fontFamily:T.sans }}>Status</p>
              <Badge variant={paymentBadge.variant}>{paymentBadge.label}</Badge>
            </div>
          </div>

          <Div my={4}/>

          <div style={{ padding:"14px 0" }}>
            <div style={{ display:"flex", justifyContent:"space-between", padding:"6px 0" }}>
              <span style={{ fontSize:13, color:T.textMid, fontFamily:T.sans }}>{job.description || "Service"}</span>
              <span style={{ fontSize:13, fontWeight:600, color:T.text, fontFamily:T.sans }}>{fmtKsh(subtotal)}</span>
            </div>
            <div style={{ display:"flex", justifyContent:"space-between", padding:"6px 0" }}>
              <span style={{ fontSize:12, color:T.faint, fontFamily:T.sans }}>Includes JiraniApp service fee ({Math.round(serviceFeePct*100)}%)</span>
              <span style={{ fontSize:12, color:T.faint, fontFamily:T.sans }}>{fmtKsh(serviceFee)}</span>
            </div>
          </div>

          <Div my={4}/>

          <div style={{ display:"flex", justifyContent:"space-between", padding:"12px 0 0" }}>
            <span style={{ fontSize:15, fontWeight:700, color:T.text, fontFamily:T.sans }}>Total</span>
            <span style={{ fontSize:19, fontWeight:800, color:T.text, fontFamily:T.sans, letterSpacing:"-0.02em" }}>{fmtKsh(total)}</span>
          </div>

          {job.location && (
            <div style={{ marginTop:18, display:"flex", gap:8, alignItems:"flex-start", background:T.card2, borderRadius:T.r10, padding:"11px 13px" }}>
              <MapPin size={14} color={T.faint} strokeWidth={2} style={{ flexShrink:0, marginTop:1 }}/>
              <span style={{ fontSize:12.5, color:T.sub, fontFamily:T.sans, lineHeight:1.5 }}>{job.location}</span>
            </div>
          )}
          {job.notes && (
            <div style={{ marginTop:10, display:"flex", gap:8, alignItems:"flex-start" }}>
              <Info size={14} color={T.faint} strokeWidth={2} style={{ flexShrink:0, marginTop:1 }}/>
              <span style={{ fontSize:12.5, color:T.sub, fontFamily:T.sans, lineHeight:1.5 }}>{job.notes}</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function BookingsModal({ open, onClose, bookings, pro }) {
  const [selected, setSelected] = useState(null);
  useEffect(() => { if (open) setSelected(null); }, [open]);
  const sorted = useMemo(() => [...bookings].sort((a,b) => new Date(b.scheduled_time||b.created_at) - new Date(a.scheduled_time||a.created_at)), [bookings]);

  return (
    <Modal open={open} onClose={onClose} eyebrow={selected ? "Invoice" : "All your bookings"} title={selected ? "Invoice" : "Bookings"} width={560}>
      {selected ? (
        <InvoiceView job={selected} pro={pro} onBack={()=>setSelected(null)} />
      ) : sorted.length === 0 ? (
        <div style={{ textAlign:"center", padding:"30px 4px" }}>
          <Calendar size={24} color={T.ghost} strokeWidth={1.5} style={{marginBottom:10}} />
          <p style={{ fontSize:13, color:T.faint, fontFamily:T.sans, margin:0 }}>No bookings yet.</p>
        </div>
      ) : (
        <div style={{ display:"flex", flexDirection:"column", gap:9 }}>
          {sorted.map(job => (
            <div key={job.id} className="pd-job-card" onClick={()=>setSelected(job)}>
              <div style={{ width:44, textAlign:"center", flexShrink:0 }}>
                <p style={{ fontSize:15, fontWeight:800, color:T.text, margin:0, fontFamily:T.sans, letterSpacing:"-0.02em", lineHeight:1 }}>{fmtShort(job.scheduled_time).split(" ")[0]}</p>
                <p style={{ fontSize:10.5, fontWeight:600, color:T.sub, margin:0, fontFamily:T.sans, textTransform:"uppercase" }}>{fmtDay(job.scheduled_time)}</p>
              </div>
              <div style={{ width:1, height:36, background:T.border, flexShrink:0 }}/>
              <Avatar name={job.customer?.full_name||"Client"} url={job.customer?.avatar_url} size={38}/>
              <div style={{ flex:1, minWidth:0 }}>
                <p style={{ fontSize:13, fontWeight:700, color:T.text, margin:"0 0 2px", fontFamily:T.sans, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{job.description || "Service"}</p>
                <p style={{ fontSize:12, color:T.faint, margin:0, fontFamily:T.sans }}>{job.customer?.full_name || "Client"}</p>
              </div>
              <div style={{ textAlign:"right", flexShrink:0 }}>
                <p style={{ fontSize:13, fontWeight:800, color:T.text, margin:"0 0 5px", fontFamily:T.sans }}>{fmtKsh(job.estimated_price)}</p>
                <Badge variant={job.payment_status==="paid" ? "green" : "amber"} size="sm">{job.payment_status==="paid" ? "Paid" : "Pending"}</Badge>
              </div>
              <ChevronRight size={16} color={T.ghost} strokeWidth={2} style={{flexShrink:0}} />
            </div>
          ))}
        </div>
      )}
    </Modal>
  );
}

/* ═══════════════════════════════════════════════════════════
   MESSAGES DROPDOWN — real unread count via RPC, click a
   summary opens that specific conversation.
═══════════════════════════════════════════════════════════ */
function MessagesDropdown({ userId, onClose }) {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data, error } = await supabase.rpc("jirani_message_summaries", { p_user_id: userId, p_limit: 8 });
        if (error) throw error;
        if (!cancelled) setItems(data || []);
      } catch (e) {
        if (!cancelled) setError(e.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [userId]);

  const openThread = async (item) => {
    try { await supabase.rpc("jirani_mark_conversation_read", { p_conversation_id: item.conversation_id, p_user_id: userId }); } catch { /* non-fatal */ }
    onClose();
    navigate(`/messages/${item.conversation_id}`);
  };

  return (
    <div onClick={e=>e.stopPropagation()} style={{
      position:"absolute", top:"calc(100% + 10px)", right:0, width:340, maxHeight:420, overflowY:"auto",
      background:T.white, border:`1.5px solid ${T.border}`, borderRadius:T.r16, boxShadow:T.s3, zIndex:60,
      animation:"jpPopIn 0.16s ease-out",
    }} className="jss-scroll">
      <div style={{ padding:"14px 16px", borderBottom:`1px solid ${T.borderFaint}`, display:"flex", alignItems:"center", justifyContent:"space-between" }}>
        <span style={{ fontSize:14, fontWeight:700, color:T.text, fontFamily:T.sans }}>Messages</span>
        <button onClick={()=>{ onClose(); navigate("/messages"); }} style={{ fontSize:11.5, fontWeight:700, color:T.green, background:"none", border:"none", cursor:"pointer", fontFamily:T.sans }}>View all</button>
      </div>
      {loading ? (
        <div style={{ padding:24, textAlign:"center", color:T.faint, fontFamily:T.sans, fontSize:12.5 }}>Loading…</div>
      ) : error ? (
        <div style={{ padding:20, textAlign:"center", color:T.faint, fontFamily:T.sans, fontSize:12 }}>Couldn't load messages right now.</div>
      ) : items.length === 0 ? (
        <div style={{ padding:24, textAlign:"center", color:T.faint, fontFamily:T.sans, fontSize:12.5 }}>No conversations yet.</div>
      ) : (
        items.map(item => (
          <button key={item.conversation_id} onClick={()=>openThread(item)} className="pd-dropdown-item" style={{
            width:"100%", display:"flex", alignItems:"flex-start", gap:10, padding:"12px 16px", background:"none",
            border:"none", borderBottom:`1px solid ${T.borderFaint}`, cursor:"pointer", textAlign:"left", transition:T.tx,
          }}>
            <Avatar name="Client" size={32}/>
            <div style={{ flex:1, minWidth:0 }}>
              <div style={{ display:"flex", justifyContent:"space-between", gap:8 }}>
                <span style={{ fontSize:12.5, fontWeight:item.unread_count>0?700:600, color:T.text, fontFamily:T.sans }}>Conversation</span>
                <span style={{ fontSize:10.5, color:T.faint, fontFamily:T.sans, flexShrink:0 }}>{timeAgo(item.last_message_at)}</span>
              </div>
              <p style={{ margin:"2px 0 0", fontSize:12, color: item.unread_count>0 ? T.textMid : T.faint, fontFamily:T.sans, fontWeight: item.unread_count>0?600:400, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{item.last_message}</p>
            </div>
            {item.unread_count > 0 && <span style={{ minWidth:16, height:16, borderRadius:"50%", background:T.red, color:"#fff", fontSize:9.5, fontWeight:700, display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0, marginTop:2 }}>{item.unread_count}</span>}
          </button>
        ))
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════
   NOTIFICATIONS DROPDOWN — real rows from `notifications`,
   click marks read and follows link_to.
═══════════════════════════════════════════════════════════ */
function NotificationsDropdown({ userId, onClose, onUnreadChange }) {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data, error } = await supabase
          .from("notifications")
          .select("id, type, subtype, title, body, link_to, read, created_at")
          .eq("recipient_id", userId)
          .order("created_at", { ascending: false })
          .limit(10);
        if (error) throw error;
        if (!cancelled) setItems(data || []);
      } catch {
        if (!cancelled) setItems([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [userId]);

  const openNotification = async (n) => {
    if (!n.read) {
      setItems(items => items.map(x => x.id === n.id ? { ...x, read: true } : x));
      onUnreadChange?.(-1);
      try { await supabase.from("notifications").update({ read: true, status: "read" }).eq("id", n.id); } catch {}
    }
    onClose();
    if (n.link_to) navigate(n.link_to);
  };

  const iconFor = (subtype) => {
    if (!subtype) return <Bell size={13} color={T.blue} strokeWidth={2}/>;
    if (subtype.includes("approved")) return <CheckCircle2 size={13} color={T.green} strokeWidth={2}/>;
    if (subtype.includes("rejected")) return <XCircle size={13} color={T.red} strokeWidth={2}/>;
    return <Clock size={13} color={T.amber} strokeWidth={2}/>;
  };

  return (
    <div onClick={e=>e.stopPropagation()} style={{
      position:"absolute", top:"calc(100% + 10px)", right:0, width:340, maxHeight:420, overflowY:"auto",
      background:T.white, border:`1.5px solid ${T.border}`, borderRadius:T.r16, boxShadow:T.s3, zIndex:60,
      animation:"jpPopIn 0.16s ease-out",
    }} className="jss-scroll">
      <div style={{ padding:"14px 16px", borderBottom:`1px solid ${T.borderFaint}` }}>
        <span style={{ fontSize:14, fontWeight:700, color:T.text, fontFamily:T.sans }}>Notifications</span>
      </div>
      {loading ? (
        <div style={{ padding:24, textAlign:"center", color:T.faint, fontFamily:T.sans, fontSize:12.5 }}>Loading…</div>
      ) : items.length === 0 ? (
        <div style={{ padding:24, textAlign:"center", color:T.faint, fontFamily:T.sans, fontSize:12.5 }}>You're all caught up.</div>
      ) : (
        items.map(n => (
          <button key={n.id} onClick={()=>openNotification(n)} className="pd-dropdown-item" style={{
            width:"100%", display:"flex", alignItems:"flex-start", gap:10, padding:"12px 16px", background: n.read ? "none" : T.greenTint,
            border:"none", borderBottom:`1px solid ${T.borderFaint}`, cursor:"pointer", textAlign:"left", transition:T.tx,
          }}>
            <span style={{ width:26, height:26, borderRadius:"50%", background:T.card2, display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0, marginTop:1 }}>{iconFor(n.subtype)}</span>
            <div style={{ flex:1, minWidth:0 }}>
              <p style={{ margin:0, fontSize:12.5, fontWeight: n.read?600:700, color:T.text, fontFamily:T.sans }}>{n.title}</p>
              {n.body && <p style={{ margin:"2px 0 0", fontSize:11.5, color:T.sub, fontFamily:T.sans, lineHeight:1.4 }}>{n.body}</p>}
              <p style={{ margin:"3px 0 0", fontSize:10.5, color:T.faint, fontFamily:T.sans }}>{timeAgo(n.created_at)}</p>
            </div>
            {!n.read && <span style={{ width:7, height:7, borderRadius:"50%", background:T.green, flexShrink:0, marginTop:5 }}/>}
          </button>
        ))
      )}
    </div>
  );
}

/* ─────────────────────────────────────
   SIDEBAR
───────────────────────────────────── */
function SidebarIllustration({ label }) {
  return (
    <div style={{
      margin: "14px 12px 0", borderRadius: T.r14,
      background: `linear-gradient(155deg, ${T.greenDeep} 0%, #0f3a22 60%, ${T.green} 130%)`,
      padding: "18px 16px", position: "relative", overflow: "hidden",
    }}>
      <div style={{ position: "absolute", top: -30, right: -30, width: 100, height: 100, borderRadius: "50%", background: "radial-gradient(circle, rgba(255,255,255,0.14), transparent 70%)", animation: "jpFloat 5s ease-in-out infinite" }} />
      <Sparkles size={16} color="#7fe3ac" strokeWidth={1.8} style={{ marginBottom: 8 }} />
      <p style={{ fontSize: 12, color: "#fff", fontFamily: T.sans, fontWeight: 700, margin: "0 0 3px", lineHeight: 1.4 }}>Your dashboard is working</p>
      <p style={{ fontSize: 11, color: "rgba(255,255,255,0.65)", fontFamily: T.sans, lineHeight: 1.5, margin: 0 }}>{label}</p>
    </div>
  );
}

function Sidebar({ pro, activeTab, onNavigate, onOpenServices, onOpenPortfolio, onAvatarClick }) {
  const navigate = useNavigate();
  const { logoutUser } = useAuth();

  const navItems = [
    { id: "overview", Icon: Home,      label: "Overview" },
    { id: "earnings", Icon: Wallet,    label: "Earnings & payouts" },
    { id: "bookings", Icon: Calendar,  label: "Bookings & schedule" },
  ];

  const actionItems = [
    { id: "photos",   Icon: Camera,     label: "Photos & videos",  onClick: onOpenPortfolio },
    { id: "services", Icon: Briefcase,  label: "Manage services",  onClick: onOpenServices },
  ];

  const growthItem = { id: "growth", Icon: Award, label: "Grow your profile" };

  const NavItem = ({ item }) => {
    const active = activeTab === item.id;
    return (
      <button
        className={!active ? "pd-nav-item" : ""}
        onClick={() => onNavigate(item.id)}
        style={{
          width: "100%", padding: "9px 12px", display: "flex", alignItems: "center", gap: 11,
          borderRadius: T.r10, fontFamily: T.sans, cursor: "pointer", transition: T.tx, border: "none",
          background: active ? T.white : "transparent", boxShadow: active ? T.s1 : "none",
          margin: "1px 0", boxSizing: "border-box",
        }}
      >
        <span style={{
          width: 30, height: 30, borderRadius: T.r10, flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center",
          background: active ? T.greenTint : T.white, border: `1px solid ${active ? T.greenBd : T.border}`,
        }}>
          <item.Icon size={14} strokeWidth={1.9} color={active ? T.green : T.faint} />
        </span>
        <span style={{ flex: 1, textAlign: "left", fontSize: 13, fontWeight: active ? 700 : 500, color: active ? T.text : T.sub }}>{item.label}</span>
        {active && <ChevronRight size={13} color={T.green} strokeWidth={2} />}
      </button>
    );
  };

  const ActionNavItem = ({ item }) => (
    <button
      className="pd-nav-item"
      onClick={item.onClick}
      style={{
        width: "100%", padding: "9px 12px", display: "flex", alignItems: "center", gap: 11,
        borderRadius: T.r10, fontFamily: T.sans, cursor: "pointer", transition: T.tx, border: "none",
        background: "transparent", margin: "1px 0", boxSizing: "border-box",
      }}
    >
      <span style={{
        width: 30, height: 30, borderRadius: T.r10, flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center",
        background: T.white, border: `1px solid ${T.border}`,
      }}>
        <item.Icon size={14} strokeWidth={1.9} color={T.faint} />
      </span>
      <span style={{ flex: 1, textAlign: "left", fontSize: 13, fontWeight: 500, color: T.sub }}>{item.label}</span>
    </button>
  );

  return (
    <div style={{
      width: 250, flexShrink: 0, background: T.card2, borderRight: `1px solid ${T.border}`,
      height: "100vh", position: "sticky", top: 0, display: "flex", flexDirection: "column",
      padding: "18px 12px 14px",
    }}>
      <div onClick={()=>navigate("/")} style={{ cursor:"pointer", padding: "2px 8px 16px" }}>
        <AppLogo />
      </div>

      <div style={{ fontSize: 10.5, fontWeight: 700, color: T.faint, textTransform: "uppercase", letterSpacing: "0.08em", fontFamily: T.sans, padding: "0 8px", marginBottom: 8 }}>Menu</div>
      <div className="jss-scroll" style={{ flex: 1, overflowY: "auto", display: "flex", flexDirection: "column", gap: 2 }}>
        {navItems.map(item => <NavItem key={item.id} item={item} />)}
        {actionItems.map(item => <ActionNavItem key={item.id} item={item} />)}
        <NavItem item={growthItem} />
      </div>

      <div style={{ borderTop: `1px solid ${T.border}`, paddingTop: 8, marginTop: 8 }}>
        <button onClick={() => { logoutUser?.(); navigate("/"); }} style={{ width: "100%", padding: "9px 12px", display: "flex", alignItems: "center", gap: 11, borderRadius: T.r10, fontFamily: T.sans, cursor: "pointer", border: "none", background: "transparent" }} className="pd-nav-item">
          <span style={{ width: 30, height: 30, borderRadius: T.r10, flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center", background: T.redTint, border: `1px solid ${T.redBd}` }}>
            <LogOut size={14} strokeWidth={1.9} color={T.red} />
          </span>
          <span style={{ flex: 1, textAlign: "left", fontSize: 13, fontWeight: 600, color: T.red }}>Sign out</span>
        </button>
      </div>

      <SidebarIllustration label={pro?.member_since ? `Member since ${pro.member_since}` : "Complete your profile to start getting more bookings."} />
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════
   NEW: ProAccountMenu — avatar+chevron dropdown with pro links
═══════════════════════════════════════════════════════════ */
function ProAccountMenu({ pro, navigate, onSignOut, onOpenServices, onOpenPortfolio }) {
  const [open, setOpen] = useState(false);
  const [hovIdx, setHovIdx] = useState(null);
  const wrapRef = useRef(null);
  const { isMobile } = useBreakpoint();
  const avatarSize = isMobile ? 26 : 32;
  const displayName = pro?.jirani_name || pro?.full_name || "Your account";

  useEffect(() => {
    const h = e => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  const items = [
    { label: "Photos & videos",         Icon: Camera,     action: onOpenPortfolio },
    { label: "Manage services",         Icon: Briefcase,  action: onOpenServices },
    { label: "Earnings & payouts",      Icon: Wallet,     action: () => navigate("/pro/earnings") },
    { label: "Help & Support",          Icon: HelpCircle, action: () => navigate("/help") },
    { label: "Switch to customer view", Icon: Users,      action: () => navigate("/search") },
    { label: "How customers see you",   Icon: Eye,        action: () => navigate(`/pro/${pro.id}`) },
  ];

  return (
    <div ref={wrapRef} style={{ position: "relative", flexShrink: 0 }}>
      <button onClick={() => setOpen(o => !o)} style={{
        display: "flex", alignItems: "center", gap: isMobile ? 4 : 6, background: "none",
        border: "none", cursor: "pointer", padding: 2, borderRadius: 999,
        boxShadow: `0 0 0 2px ${open ? T.green : T.greenBd}`, transition: T.tx,
      }}>
        <div style={{ position: "relative" }}>
          <Avatar url={pro?.avatar_url} name={displayName} size={avatarSize} />
          <span style={{
            position: "absolute", bottom: -1, right: -1,
            width: 9, height: 9, borderRadius: "50%",
            background: getAvailDot(pro?.availability),
            border: "2px solid #fff",
          }} />
        </div>
        <ChevronDown size={isMobile ? 11 : 14} color={T.faint} strokeWidth={2.2} />
      </button>

      {open && (
        <div style={{
          position: "absolute", right: 0, top: "calc(100% + 10px)", width: 252,
          background: T.white, borderRadius: T.r16, border: `1.5px solid ${T.border}`,
          boxShadow: T.s3, zIndex: 400, overflow: "hidden",
        }}>
          <div style={{ padding: "14px 16px 12px", borderBottom: `1px solid ${T.borderFaint}` }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: T.text, fontFamily: T.sans }}>{displayName}</div>
            <div style={{ fontSize: 11, color: T.faint, fontFamily: T.sans, marginTop: 1 }}>
              {pro?.pro_number ? `PRO · ${pro.pro_number}` : pro?.email || ""}
            </div>
          </div>
          <div style={{ padding: "6px 0" }}>
            {items.map((item, i) => (
              <button key={i} onMouseEnter={() => setHovIdx(i)} onMouseLeave={() => setHovIdx(null)}
                onClick={() => { item.action(); setOpen(false); }}
                style={{ width: "100%", display: "flex", alignItems: "center", gap: 10, padding: "9px 16px",
                  background: hovIdx === i ? T.card2 : "transparent", border: "none", cursor: "pointer",
                  textAlign: "left", fontFamily: T.sans, fontSize: 13, color: T.textMid, transition: "background 0.12s" }}>
                <item.Icon size={14} color={T.faint} strokeWidth={1.75} /> {item.label}
              </button>
            ))}
          </div>
          <div style={{ borderTop: `1px solid ${T.borderFaint}` }}>
            <button onMouseEnter={() => setHovIdx(99)} onMouseLeave={() => setHovIdx(null)}
              onClick={() => { onSignOut(); setOpen(false); }}
              style={{ width: "100%", display: "flex", alignItems: "center", gap: 10, padding: "10px 16px",
                background: hovIdx === 99 ? T.redTint : "transparent", border: "none", cursor: "pointer",
                fontFamily: T.sans, fontSize: 13, fontWeight: 600, color: T.red, transition: "background 0.12s" }}>
              <LogOut size={14} color={T.red} strokeWidth={1.75} /> Sign out
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

/* ─────────────────────────────────────
   TOP BAR — removed "Back to Home", search bar centered, no bottom border
───────────────────────────────────── */
function TopBar({ pro, userId, unreadMessages, unreadNotifs, onUnreadNotifsChange, onOpenServices, onOpenPortfolio, onSignOut }) {
  const navigate = useNavigate();
  const { isMobile } = useBreakpoint();
  const [q, setQ] = useState("");
  const [openDropdown, setOpenDropdown] = useState(null);

  useEffect(() => {
    if (!openDropdown) return;
    const close = () => setOpenDropdown(null);
    window.addEventListener("click", close);
    return () => window.removeEventListener("click", close);
  }, [openDropdown]);

  const btnSize   = isMobile ? 27 : 34;
  const iconSize  = isMobile ? 12 : 14;
  const badgeSize = isMobile ? 13 : 15;
  const badgeFont = isMobile ? 8  : 9;

  return (
    <div style={{ height: isMobile ? 56 : 66, padding: isMobile ? "0 16px" : "0 26px", display: "flex", alignItems: "center", justifyContent: "space-between", flexShrink: 0, background: T.white, position: "sticky", top: 0, zIndex: 50 }}>
      {isMobile ? (
        <div onClick={() => navigate("/")} style={{ cursor: "pointer" }}>
          <AppLogo size="sm" />
        </div>
      ) : (
        <div style={{ flex: 1, maxWidth: 320, position: "relative" }}>
          <Search size={14} color={T.faint} strokeWidth={2} style={{ position: "absolute", left: 13, top: "50%", transform: "translateY(-50%)" }} />
          <input value={q} onChange={e => setQ(e.target.value)} placeholder="Search here…"
            style={{ width: "100%", padding: "9px 12px 9px 34px", borderRadius: T.rFull, border: `1.5px solid ${T.border}`, background: T.card2, fontSize: 12.5, fontFamily: T.sans, outline: "none", color: T.text, boxSizing: "border-box" }} />
        </div>
      )}

      <div style={{ display: "flex", alignItems: "center", gap: isMobile ? 4 : 6 }}>
        <div style={{ position:"relative" }} onClick={e=>e.stopPropagation()}>
          <button onClick={()=>setOpenDropdown(d => d==="messages" ? null : "messages")} className="pd-icon-badge" style={{ width: btnSize, height: btnSize, borderRadius: "50%", background: T.blueTint, border: `1px solid ${T.blueBd}`, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", transition: T.tx, position:"relative" }}>
            <MessageCircle size={iconSize} color={T.blue} strokeWidth={2} />
            {unreadMessages > 0 && <span style={{ position:"absolute", top: isMobile?-2:-3, right: isMobile?-2:-3, minWidth:badgeSize, height:badgeSize, borderRadius:"50%", background:T.red, color:"#fff", fontSize:badgeFont, fontWeight:700, display:"flex", alignItems:"center", justifyContent:"center", border:"1.5px solid #fff" }}>{unreadMessages > 9 ? "9+" : unreadMessages}</span>}
          </button>
          {openDropdown === "messages" && <MessagesDropdown userId={userId} onClose={()=>setOpenDropdown(null)} />}
        </div>

        <div style={{ position:"relative" }} onClick={e=>e.stopPropagation()}>
          <button onClick={()=>setOpenDropdown(d => d==="notifications" ? null : "notifications")} className="pd-icon-badge" style={{ width: btnSize, height: btnSize, borderRadius: "50%", background: T.goldTint, border: `1px solid ${T.goldBd}`, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", transition: T.tx, position:"relative" }}>
            <Bell size={iconSize} color={T.gold} strokeWidth={2} />
            {unreadNotifs > 0 && <span style={{ position:"absolute", top: isMobile?-2:-3, right: isMobile?-2:-3, minWidth:badgeSize, height:badgeSize, borderRadius:"50%", background:T.red, color:"#fff", fontSize:badgeFont, fontWeight:700, display:"flex", alignItems:"center", justifyContent:"center", border:"1.5px solid #fff" }}>{unreadNotifs > 9 ? "9+" : unreadNotifs}</span>}
          </button>
          {openDropdown === "notifications" && <NotificationsDropdown userId={userId} onClose={()=>setOpenDropdown(null)} onUnreadChange={onUnreadNotifsChange} />}
        </div>

        <ProAccountMenu pro={pro} navigate={navigate} onSignOut={onSignOut} onOpenServices={onOpenServices} onOpenPortfolio={onOpenPortfolio} />
      </div>
    </div>
  );
}

/* ─────────────────────────────────────
   TABS
───────────────────────────────────── */
function Tabs({ tabs, active, onChange }) {
  return (
    <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 22 }}>
      {tabs.map(t => (
        <button key={t.id} className="pd-tab" onClick={()=>onChange(t.id)} style={{
          height: 34, padding: "0 16px", borderRadius: T.rFull,
          border: active === t.id ? "none" : `1.5px solid ${T.border}`,
          fontSize: 12.5, fontWeight: active === t.id ? 700 : 500,
          fontFamily: T.sans, cursor: "pointer",
          background: active === t.id ? T.text : T.white,
          color: active === t.id ? "#fff" : T.sub,
          display: "flex", alignItems: "center", gap: 6,
        }}>
          <t.Icon size={13} strokeWidth={2} />
          {t.label}
        </button>
      ))}
    </div>
  );
}

/* ─────────────────────────────────────
   PROFILE HEADER
───────────────────────────────────── */
function ProfileHeader({ pro, completionPct, trustLabel, trustColor, onFinishSetup }) {
  const { isMobile } = useBreakpoint();
  const displayName = pro.jirani_name || (pro.full_name || "there").split(" ")[0];

  const stats = [
    { label:"Rating", value: pro.avg_rating ? pro.avg_rating.toFixed(1) : "—", icon:<Star size={13} color={T.green} fill={T.green} /> },
    { label:"Jobs done", value: pro.completed_jobs ?? 0, icon:<CheckCircle2 size={13} color={T.green} strokeWidth={2.2} /> },
    { label:"Success", value: `${pro.success_score ?? "—"}%`, icon:<TrendingUp size={13} color={T.green} strokeWidth={2.2} /> },
    { label:"Response", value: pro.response_minutes ? `<${pro.response_minutes}m` : "—", icon:<Zap size={13} color={T.green} strokeWidth={2.2} /> },
  ];

  return (
    <div style={{ marginBottom: 24 }}>
      <div style={{ display:"flex", alignItems:"flex-start", justifyContent:"space-between", gap:16, flexWrap:"wrap", marginBottom: 20 }}>
        <div>
          <h1 style={{ fontSize: isMobile?26:32, fontWeight:400, fontStyle:"italic", color:T.text, margin:0, fontFamily:T.serif, letterSpacing:"-0.01em", lineHeight:1.1, display:"flex", alignItems:"center", gap:9, flexWrap:"wrap" }}>
            Hey, {displayName} <BadgeCheck size={22} color={T.green} fill={T.greenTint} strokeWidth={2} />
          </h1>
          <p style={{ fontSize:12.5, color:T.sub, margin:"6px 0 0", fontFamily:T.sans, display:"flex", alignItems:"center", gap:6, flexWrap:"wrap" }}>
            {pro.title || "Home services professional"}
            {pro.city && <><span style={{color:T.ghost}}>·</span><MapPin size={11} strokeWidth={2}/> {pro.city}</>}
            <Badge variant={trustColor} size="sm">{trustLabel}</Badge>
          </p>
        </div>
        {!isMobile && (
  <div style={{
    display:"flex", alignItems:"center", gap:18,
    background:`linear-gradient(135deg, ${T.white} 0%, ${T.greenTint} 160%)`,
    border:`1.5px solid ${T.greenBd}`, borderRadius:T.r16, padding:"16px 22px",
    boxShadow:T.s2, position:"relative", overflow:"hidden", minWidth:280,
  }}>
    <CompletionRing pct={completionPct} size={58} />
    <div>
      <span style={{ fontSize:10, fontWeight:700, color:T.green, textTransform:"uppercase", letterSpacing:"0.08em", fontFamily:T.sans, display:"block", marginBottom:3 }}>Your profile</span>
      <p style={{ fontSize:15, fontWeight:800, color:T.text, fontFamily:T.sans, margin:"0 0 8px" }}>
        {completionPct >= 80 ? "Almost fully set up" : completionPct >= 50 ? "Good progress so far" : "Let's get you booking"}
      </p>
      <button onClick={onFinishSetup} style={{
        display:"inline-flex", alignItems:"center", gap:6,
        background:T.green, color:"#fff", border:"none", borderRadius:T.rFull,
        padding:"7px 16px", fontSize:12.5, fontWeight:700, fontFamily:T.sans,
        cursor:"pointer", boxShadow:"0 3px 10px rgba(30,146,84,0.28)",
      }}>
        Finish setup <ArrowUpRight size={13} strokeWidth={2.4}/>
      </button>
    </div>
  </div>
)}
      </div>

      {isMobile && (
        <div style={{ display:"flex", alignItems:"center", gap:14, background:T.white, border:`1.5px solid ${T.border}`, borderRadius:T.r16, padding:"14px 16px", boxShadow:T.s1, marginBottom:14 }}>
          <CompletionRing pct={completionPct} size={44} />
          <div style={{ flex:1, minWidth:0 }}>
            <p style={{ margin:"0 0 2px", fontSize:13, fontWeight:700, color:T.text, fontFamily:T.sans }}>Profile completion</p>
            <p style={{ margin:0, fontSize:11.5, color:T.sub, fontFamily:T.sans }}>Finish setup to get more bookings</p>
          </div>
          <button onClick={onFinishSetup} style={{ background:"none", border:"none", cursor:"pointer", display:"flex", alignItems:"center", gap:3, fontSize:12.5, fontWeight:700, color:T.green, fontFamily:T.sans, flexShrink:0, whiteSpace:"nowrap" }}>
            Finish <ArrowUpRight size={13} strokeWidth={2.4}/>
          </button>
        </div>
      )}

      <div style={{ display:"flex", background:T.white, border:`1.5px solid ${T.border}`, borderRadius:T.r14 }}>
        {stats.map((s, i) => (
          <div key={s.label} style={{ flex:1, textAlign:"center", padding:"14px 8px", borderLeft: i>0 ? `1px solid ${T.borderFaint}` : "none" }}>
            <span style={{ color:T.green, display:"inline-flex" }}>{s.icon}</span>
            <p style={{ margin:"6px 0 1px", fontSize:16, fontWeight:800, color:T.text, fontFamily:T.sans }}>{s.value}</p>
            <p style={{ margin:0, fontSize:9.5, color:T.faint, fontFamily:T.sans, fontWeight:700, textTransform:"uppercase" }}>{s.label}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ─────────────────────────────────────
   TODAY'S FOCUS
───────────────────────────────────── */
function TodaysFocus({ nextJob, onMessage, onViewDetails, onOpenAvailability }) {
  const { isMobile } = useBreakpoint();

  if (!nextJob) {
    return (
      <Section eyebrow="Right now" title="Today's focus">
        <div>
          <p style={{ fontSize:14, color:T.textMid, fontFamily:T.sans, margin:"0 0 4px", fontWeight:500 }}>
            Nothing on the books right now.
          </p>
          <p style={{ fontSize:12.5, color:T.faint, fontFamily:T.sans, margin:"0 0 16px" }}>
            New booking requests will show up here the moment a client sends one.
          </p>
          <Btn v="outline" size="md" onClick={onOpenAvailability}>Update availability</Btn>
        </div>
      </Section>
    );
  }

  return (
    <div className="pd-card" style={{
      padding: isMobile?"20px 18px":"26px 28px",
      background:`linear-gradient(150deg, ${T.greenDeep} 0%, #0f3a22 55%, ${T.greenDark} 100%)`,
      border:"none", position:"relative", overflow:"hidden",
    }}>
      <div style={{ position:"absolute", top:-60, right:-60, width:220, height:220, borderRadius:"50%", background:"radial-gradient(circle, rgba(30,146,84,0.35), transparent 70%)" }} />
      <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:18, position:"relative" }}>
        <p style={{ margin:0, fontSize:10, fontWeight:700, color:"#7fe3ac", textTransform:"uppercase", letterSpacing:"0.1em", fontFamily:T.sans, display:"flex", alignItems:"center", gap:6 }}>
          <Zap size={12} strokeWidth={2.2} fill="#7fe3ac" /> Next up
        </p>
        <Badge variant="dark">Confirmed</Badge>
      </div>

      <div style={{ display:"flex", flexDirection: isMobile?"column":"row", justifyContent:"space-between", gap: isMobile?18:24, marginBottom:22, position:"relative" }}>
        <div style={{ flex:1, minWidth:0 }}>
          <p style={{ fontSize: isMobile?20:26, fontWeight:400, fontStyle:"italic", color:"#fff", margin:"0 0 8px", lineHeight:1.15, fontFamily:T.serif, letterSpacing:"-0.02em" }}>
            {nextJob.description || "Booking"}
          </p>
          <p style={{ fontSize:12.5, color:"rgba(255,255,255,0.68)", margin:0, fontFamily:T.sans, display:"flex", alignItems:"center", gap:8, flexWrap:"wrap" }}>
            {nextJob.customer?.full_name || "Client"} <span style={{opacity:0.5}}>·</span> <strong style={{color:"#fff", fontWeight:700}}>{fmtKsh(nextJob.estimated_price)}</strong>
          </p>
        </div>
        <div style={{ textAlign: isMobile?"left":"right", flexShrink:0 }}>
          <p style={{ fontSize:9.5, fontWeight:700, color:"rgba(255,255,255,0.5)", margin:"0 0 4px", fontFamily:T.sans, textTransform:"uppercase", letterSpacing:"0.1em" }}>Starts in</p>
          <Countdown targetIso={nextJob.scheduled_time} dark />
        </div>
      </div>
      <div style={{ display:"flex", alignItems:"center", gap:10, flexWrap:"wrap", position:"relative" }}>
        <button className="pd-btn" onClick={onViewDetails} style={{
          height:40, padding:"0 22px", borderRadius:T.rFull, border:"none", background:"#fff", color:T.greenDeep,
          fontSize:13, fontWeight:700, fontFamily:T.sans, cursor:"pointer",
        }}>View details</button>
        <button className="pd-btn" onClick={onMessage} style={{
          height:40, padding:"0 20px", borderRadius:T.rFull, border:"1px solid rgba(255,255,255,0.28)", background:"rgba(255,255,255,0.08)",
          color:"#fff", fontSize:13, fontWeight:600, fontFamily:T.sans, cursor:"pointer", display:"flex", alignItems:"center", gap:7,
        }}><MessageCircle size={14} strokeWidth={2}/> Message client</button>
      </div>
    </div>
  );
}

/* Four JiraniApp pro tiers. Job-count ranges are gated by a matching
   rating floor, so a high job count with a weak rating stays at Beginner
   rather than auto-advancing — tell me if you'd rather a pro "freeze" at
   their last-earned tier instead of dropping back. business_pro_approved
   isn't selected from pro_profiles yet (column doesn't exist), so it's
   always falsy here until that migration lands — nobody reaches Business
   Pro until then, which is the safe default. */
function getProLevel(pro) {
  const jobs = pro?.completed_jobs || 0;
  const rating = pro?.avg_rating || 0;
  const approved = !!pro?.business_pro_approved;

  if (jobs >= 150 && rating >= 4.8 && approved) return { label: "Business Pro", variant: "gold" };
  if (jobs >= 50  && jobs < 150   && rating >= 4.7) return { label: "Jirani Senior", variant: "blue" };
  if (jobs >= 25  && jobs < 50    && rating >= 4.6) return { label: "Jirani Junior", variant: "green" };
  return { label: "Jirani Beginner", variant: "neutral" };
}

function ratingWord(v, thresholds) {
  const [good, ok] = thresholds;
  if (v >= good) return { label:"Excellent", variant:"green" };
  if (v >= ok)   return { label:"Good",      variant:"blue"  };
  return { label:"Needs work", variant:"amber" };
}

function PerformanceSnapshot({ pro }) {
  const rows = [
    { label:"Response time",   val: ratingWord(pro.response_minutes ? (60 - pro.response_minutes) : 0, [45,20]) },
    { label:"Reply rate",      val: ratingWord(pro.response_rate || 0, [90,70]) },
    { label:"Job success",     val: ratingWord(pro.success_score || 0, [90,75]) },
    { label:"Repeat clients",  val: ratingWord(pro.repeat_rate || 0, [35,15]) },
  ];
  return (
    <Section eyebrow="How clients see you" title="Performance snapshot"
      sub="The same signals JiraniApp uses to rank your profile in search.">
      <div style={{ display:"flex", flexDirection:"column" }}>
        {rows.map((r,i) => (
          <div key={r.label} style={{
            display:"flex", alignItems:"center", justifyContent:"space-between",
            padding:"11px 2px", borderBottom: i<rows.length-1 ? `1px solid ${T.borderFaint}` : "none",
          }}>
            <span style={{ fontSize:13, color:T.textMid, fontFamily:T.sans, fontWeight:500 }}>{r.label}</span>
            <Badge variant={r.val.variant} size="sm">{r.val.label}</Badge>
          </div>
        ))}
      </div>
    </Section>
  );
}

function ServicesSummary({ services, onAddService, onEditService }) {
  return (
    <Section eyebrow="Your listings" title="Services & pricing"
      right={<Btn v="outline" size="sm" onClick={onAddService} icon={<Plus size={13} strokeWidth={2.4}/>}>Add a service</Btn>}>
      {services.length === 0 ? (
        <div style={{ textAlign:"center", padding:"14px 4px" }}>
          <p style={{ fontSize:13, color:T.faint, fontFamily:T.sans, margin:"0 0 10px" }}>
            You haven't added any services yet — clients can't book you until you do.
          </p>
          <Btn v="outline" size="sm" onClick={onAddService}>Add a service</Btn>
        </div>
      ) : (
        <div style={{ display:"flex", flexDirection:"column", gap:6 }}>
          {services.map(s => (
            <div key={s.id} onClick={()=>onEditService(s)} className="pd-job-card" style={{
              display:"flex", alignItems:"center", justifyContent:"space-between",
              padding:"11px 13px", borderRadius:T.r10, border:`1.5px solid ${T.border}`, gap:10, cursor:"pointer",
            }}>
              <div style={{ minWidth:0 }}>
                <div style={{ display:"flex", alignItems:"center", gap:6 }}>
                  <p style={{ margin:0, fontSize:12.5, fontWeight:700, color:T.text, fontFamily:T.sans, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{s.name}</p>
                  {s.status === "pending" && <Badge variant="amber" size="sm">Pending review</Badge>}
                  {s.status === "rejected" && <Badge variant="red" size="sm">Not approved</Badge>}
                </div>
                <p style={{ margin:"2px 0 0", fontSize:11, color:T.faint, fontFamily:T.sans }}>{s.duration_minutes ? `${s.duration_minutes} min` : s.category}</p>
                {s.status === "rejected" && s.rejection_reason && <p style={{ margin:"3px 0 0", fontSize:11, color:T.red, fontFamily:T.sans }}>{s.rejection_reason}</p>}
              </div>
              <span style={{ fontSize:13.5, fontWeight:800, color:T.text, fontFamily:T.sans, flexShrink:0 }}>{fmtKsh(s.price)}</span>
            </div>
          ))}
        </div>
      )}
    </Section>
  );
}

function TrustSection({ pro, onManage }) {
  return (
    <Section eyebrow="Trust & safety" title="Verification status"
      right={<Btn v="outline" size="sm" onClick={onManage}>Manage</Btn>}>
      <div style={{display:"flex",flexDirection:"column",gap:10}}>
        <VerifyRow label="Identity verified"  ok={!!pro.verified}/>
        <VerifyRow label="Phone verified"      ok={!!pro.phone_verified}/>
        <VerifyRow label="Background checked"  ok={!!pro.background_checked}/>
      </div>
      {pro.member_since && (
        <p style={{margin:"14px 0 0",paddingTop:12,borderTop:`1px solid ${T.borderFaint}`,fontSize:11.5,color:T.faint,fontFamily:T.sans, display:"flex", alignItems:"center", gap:6}}>
          <ShieldCheck size={13} strokeWidth={1.8} /> Member since {pro.member_since}
        </p>
      )}
    </Section>
  );
}

function SkillsSection({ skills }) {
  const navigate = useNavigate();
  if (!skills.length) return null;
  return (
    <Section eyebrow="Expertise" title="Skills & specialisms"
      right={<Btn v="outline" size="sm" onClick={()=>navigate("/pro-onboarding?tab=profile")}>Edit</Btn>}>
      <div style={{ display:"flex", flexWrap:"wrap", gap:6 }}>
        {skills.map(sk => <SkillPill key={sk} label={sk}/>)}
      </div>
    </Section>
  );
}

function ActivityFeed({ events, onOpenBookings }) {
  return (
    <Section eyebrow="What's happened" title="Recent activity">
      {events.length === 0 ? (
        <p style={{ fontSize:13, color:T.faint, fontFamily:T.sans }}>No activity yet.</p>
      ) : (
        <div style={{ display:"flex", flexDirection:"column" }}>
          {events.slice(0,6).map((e,i) => (
            <div key={e.id} className="pd-activity-item" onClick={onOpenBookings}
              style={{ cursor:"pointer", paddingBottom: i<events.length-1?16:0, animationDelay:`${i*0.05}s` }}>
              <div style={{
                width:30, height:30, borderRadius:"50%", flexShrink:0, display:"flex", alignItems:"center", justifyContent:"center",
                background: e.type==="review" ? T.goldTint : T.greenTint, border:`1.5px solid ${e.type==="review"?T.goldBd:T.greenBd}`,
                color: e.type==="review" ? T.gold : T.green,
              }}>
                {e.type==="review" ? <Star size={13} fill={T.gold} color={T.gold} /> : <CheckCircle2 size={14} strokeWidth={2.2} />}
              </div>
              <div style={{ flex:1, minWidth:0 }}>
                <p style={{ fontSize:13, fontWeight:700, color:T.text, margin:"0 0 2px", fontFamily:T.sans }}>{e.title}</p>
                <p style={{ fontSize:12, color:T.sub, margin:"0 0 3px", fontFamily:T.sans }}>{e.sub}</p>
                {e.stars && (
                  <div style={{ display:"flex", gap:2, marginBottom:3 }}>
                    {[1,2,3,4,5].map(s => <Star key={s} size={11} fill={s<=e.stars?T.gold:T.borderFaint} color={s<=e.stars?T.gold:T.borderFaint} />)}
                  </div>
                )}
                <p style={{ fontSize:11, color:T.faint, margin:0, fontFamily:T.sans }}>{timeAgo(e.ts)}</p>
              </div>
            </div>
          ))}
        </div>
      )}
    </Section>
  );
}

/* ─────────────────────────────────────
   EARNINGS TAB
───────────────────────────────────── */
function EarningsSection({ paidJobs }) {
  const now = new Date();
  const weekAgo  = new Date(now); weekAgo.setDate(weekAgo.getDate()-7);
  const monthAgo = new Date(now); monthAgo.setMonth(monthAgo.getMonth()-1);

  const sumSince = (since) => paidJobs
    .filter(j => new Date(j.scheduled_time || j.created_at) >= since)
    .reduce((s,j) => s + (j.estimated_price||0), 0);

  const weekTotal  = sumSince(weekAgo);
  const monthTotal = sumSince(monthAgo);
  const avgPerJob  = paidJobs.length ? Math.round(paidJobs.reduce((s,j)=>s+(j.estimated_price||0),0) / paidJobs.length) : 0;

  const metrics = [
    { label:"This week",   value: fmtKsh(weekTotal),  bg:T.green },
    { label:"This month",  value: fmtKsh(monthTotal), bg:T.blue },
    { label:"Avg per job", value: fmtKsh(avgPerJob),  bg:T.gold },
  ];

  return (
    <Section eyebrow="Your income" title="Earnings">
      <div style={{ display:"flex", gap:10, flexWrap:"wrap", marginBottom:20 }}>
        {metrics.map(m => (
          <div key={m.label} style={{ flex:"1 1 140px", background:T.card2, border:`1px solid ${T.borderFaint}`, borderRadius:T.r12, padding:"13px 15px", display:"flex", alignItems:"center", gap:10 }}>
            <span style={{ width:8, height:8, borderRadius:"50%", background:m.bg, flexShrink:0 }}/>
            <div>
              <p style={{ margin:0, fontSize:16.5, fontWeight:800, color:T.text, fontFamily:T.sans, letterSpacing:"-0.02em" }}>{m.value}</p>
              <p style={{ margin:0, fontSize:9.5, color:T.faint, fontFamily:T.sans, fontWeight:700, textTransform:"uppercase", letterSpacing:"0.06em" }}>{m.label}</p>
            </div>
          </div>
        ))}
      </div>
      <EarningsChart paidJobs={paidJobs}/>
    </Section>
  );
}

function PayoutSection({ pro, paidJobs, onManage }) {
  const method = pro.payout_method;
  const hasPayout = method === "mpesa" ? !!pro.payout_mpesa_number : method === "bank" ? !!pro.payout_account_number : false;
  const masked = (v) => v ? `•••• ${String(v).slice(-3)}` : "";
  const recent = [...paidJobs]
    .sort((a,b) => new Date(b.scheduled_time||b.created_at) - new Date(a.scheduled_time||a.created_at))
    .slice(0,5);

  return (
    <Section eyebrow="Getting paid" title="Payout details"
      right={<Btn v="outline" size="sm" onClick={onManage}>{hasPayout?"Manage":"Set up"}</Btn>}>
      {hasPayout ? (
        <div className="pd-method-card" style={{
          display:"flex", alignItems:"center", gap:11, padding:"12px 14px",
          border:`1.5px solid ${T.greenBd}`, borderRadius:T.r12, marginBottom:16, background:T.greenTint, transition:T.tx,
        }}>
          <div style={{
            width:36, height:36, borderRadius:T.r10, background:T.white,
            display:"flex", alignItems:"center", justifyContent:"center", color:T.green, flexShrink:0, border:`1px solid ${T.greenBd}`,
          }}>
            {method === "mpesa" ? I.mpesa : I.card}
          </div>
          <div style={{ minWidth:0, flex:1 }}>
            <p style={{ margin:0, fontSize:12.5, fontWeight:700, color:T.text, fontFamily:T.sans }}>
              {method === "mpesa" ? "M-Pesa" : (pro.payout_bank_name || "Bank account")}
            </p>
            <p style={{ margin:"1px 0 0", fontSize:11.5, color:T.faint, fontFamily:T.sans }}>
              {method === "mpesa" ? masked(pro.payout_mpesa_number) : `${pro.payout_account_name||""} · ${masked(pro.payout_account_number)}`}
            </p>
          </div>
          <span style={{
            width:18, height:18, borderRadius:"50%", border:`2px solid ${T.green}`,
            display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0,
          }}><span style={{ width:9, height:9, borderRadius:"50%", background:T.green }} /></span>
        </div>
      ) : (
        <div style={{
          display:"flex", gap:9, padding:"12px 14px", borderRadius:T.r10, background:T.amberTint, border:`1px solid ${T.amberBd}`, marginBottom:16,
        }}>
          <AlertCircle size={15} color={T.amber} strokeWidth={2} style={{flexShrink:0, marginTop:1}} />
          <p style={{ margin:0, fontSize:12.5, color:T.amber, fontFamily:T.sans, lineHeight:1.6 }}>
            No payout method set yet. Add one so you can get paid after a job is confirmed.
          </p>
        </div>
      )}

      <p style={{ margin:"0 0 8px", fontSize:10.5, fontWeight:700, color:T.faint, fontFamily:T.sans, textTransform:"uppercase", letterSpacing:"0.08em" }}>
        Recent payouts
      </p>
      {recent.length === 0 ? (
        <p style={{ fontSize:13, color:T.faint, fontFamily:T.sans }}>No payouts yet.</p>
      ) : (
        <div style={{ display:"flex", flexDirection:"column", gap:7 }}>
          {recent.map(j => (
            <div key={j.id} style={{ display:"flex", alignItems:"center", justifyContent:"space-between" }}>
              <div style={{ minWidth:0 }}>
                <p style={{ margin:0, fontSize:12.5, color:T.textMid, fontFamily:T.sans, fontWeight:500, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{j.description || "Service"}</p>
                <p style={{ margin:"1px 0 0", fontSize:11, color:T.faint, fontFamily:T.sans }}>{fmtShort(j.scheduled_time||j.created_at)}</p>
              </div>
              <Badge variant="green" size="sm">{fmtKsh(j.estimated_price)}</Badge>
            </div>
          ))}
        </div>
      )}
    </Section>
  );
}

/* ─────────────────────────────────────
   BOOKINGS TAB
───────────────────────────────────── */
function UpcomingJobs({ jobs, onOpenBookings }) {
  return (
    <Section eyebrow="On the calendar" title="Upcoming jobs" right={<Btn v="ghost" size="sm" onClick={onOpenBookings} icon={<Calendar size={13} strokeWidth={2}/>}>See all</Btn>}>
      {jobs.length === 0 ? (
        <div style={{ textAlign:"center", padding:"18px 4px" }}>
          <Calendar size={22} color={T.ghost} strokeWidth={1.5} style={{marginBottom:8}} />
          <p style={{ fontSize:13, color:T.faint, fontFamily:T.sans, margin:0 }}>No upcoming jobs yet.</p>
        </div>
      ) : (
        <div style={{ display:"flex", flexDirection:"column", gap:9 }}>
          {jobs.slice(0,8).map(job => (
            <div key={job.id} className="pd-job-card" onClick={onOpenBookings}>
              <div style={{ width:44, textAlign:"center", flexShrink:0 }}>
                <p style={{ fontSize:15, fontWeight:800, color:T.text, margin:0, fontFamily:T.sans, letterSpacing:"-0.02em", lineHeight:1 }}>{fmtShort(job.scheduled_time).split(" ")[0]}</p>
                <p style={{ fontSize:10.5, fontWeight:600, color:T.sub, margin:0, fontFamily:T.sans, textTransform:"uppercase" }}>{fmtDay(job.scheduled_time)}</p>
                <p style={{ fontSize:10, color:T.faint, margin:"2px 0 0", fontFamily:T.sans }}>{fmtClock(job.scheduled_time)}</p>
              </div>
              <div style={{ width:1, height:36, background:T.border, flexShrink:0 }}/>
              <Avatar name={job.customer?.full_name||"Client"} url={job.customer?.avatar_url} size={40}/>
              <div style={{ flex:1, minWidth:0 }}>
                <p style={{ fontSize:13, fontWeight:700, color:T.text, margin:"0 0 2px", fontFamily:T.sans, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{job.description || "Service"}</p>
                <p style={{ fontSize:12, color:T.faint, margin:0, fontFamily:T.sans }}>{job.customer?.full_name || "Client"}</p>
              </div>
              <div style={{ textAlign:"right", flexShrink:0 }}>
                <p style={{ fontSize:13, fontWeight:800, color:T.text, margin:"0 0 5px", fontFamily:T.sans }}>{fmtKsh(job.estimated_price)}</p>
                <Badge variant={job.payment_status==="paid" ? "green" : "amber"} size="sm">{job.payment_status==="paid" ? "Confirmed" : "Pending"}</Badge>
              </div>
              <ChevronRight size={16} color={T.ghost} strokeWidth={2} style={{flexShrink:0}} />
            </div>
          ))}
        </div>
      )}
    </Section>
  );
}

function AreasAvailabilitySection({ areas, schedule, onEdit }) {
  const byDay = {};
  schedule.forEach(s => { byDay[s.day_of_week] = s; });
  const today = new Date().getDay();

  return (
    <Section eyebrow="Where & when you work" title="Service areas & availability"
      right={<Btn v="outline" size="sm" onClick={onEdit}>Edit</Btn>}>
      <div>
        <p style={{ margin:"0 0 8px", fontSize:10.5, fontWeight:700, color:T.faint, fontFamily:T.sans, textTransform:"uppercase", letterSpacing:"0.08em" }}>
          Service areas
        </p>
        {areas.length ? (
          <div style={{ display:"flex", flexWrap:"wrap", gap:6 }}>
            {areas.map(a => <Badge key={a}><MapPin size={10} strokeWidth={2}/> {a}</Badge>)}
          </div>
        ) : (
          <p style={{ fontSize:13, color:T.faint, fontFamily:T.sans }}>No service areas set yet.</p>
        )}
      </div>
      <Div my={16}/>
      <div>
        <p style={{ margin:"0 0 8px", fontSize:10.5, fontWeight:700, color:T.faint, fontFamily:T.sans, textTransform:"uppercase", letterSpacing:"0.08em" }}>
          Weekly availability
        </p>
        {schedule.length === 0 ? (
          <p style={{ fontSize:13, color:T.faint, fontFamily:T.sans }}>
            No schedule set — clients will only see your live "Available now" toggle.
          </p>
        ) : (
          <div style={{ display:"flex", flexDirection:"column", gap:1 }}>
            {[1,2,3,4,5,6,0].map(dow => {
              const d = byDay[dow];
              const isToday = dow === today;
              return (
                <div key={dow} style={{
                  display:"flex", justifyContent:"space-between", padding:"7px 8px",
                  borderRadius: isToday ? T.r8 : 0,
                  background: isToday ? T.greenTint : "transparent",
                  borderBottom: isToday ? "none" : `1px solid ${T.borderFaint}`,
                }}>
                  <span style={{ fontSize:12.5, color: isToday ? T.green : T.textMid, fontFamily:T.sans, fontWeight: isToday ? 700 : 500 }}>{WEEKDAYS[dow]}{isToday && " · Today"}</span>
                  <span style={{ fontSize:12.5, color: d && d.is_open ? (isToday ? T.green : T.text) : T.faint, fontFamily:T.sans, fontWeight: isToday ? 700 : 400 }}>
                    {d && d.is_open ? `${fmtHM(d.start_time)} – ${fmtHM(d.end_time)}` : "Closed"}
                  </span>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </Section>
  );
}

/* ─────────────────────────────────────
   GROWTH TAB
───────────────────────────────────── */
function GrowthSuggestions({ pro, services, portfolioCount, areasCount, scheduleCount, onOpenPhotos, onOpenAddService, onOpenBio, onOpenVerification, onOpenPayout, onOpenAvailability, onOpenJiraniName }) {
  const suggestions = [];

  if (portfolioCount < 5) suggestions.push({
    title:`Add ${5-portfolioCount} more photo${5-portfolioCount===1?"":"s"} to your portfolio`,
    body:"Profiles with 5+ photos get noticeably more bookings.",
    action:"Add photos", onClick:onOpenPhotos, variant:"amber", icon:<Camera size={14} strokeWidth={2}/>,
  });
  if (!services.length) suggestions.push({
    title:"Add your first service",
    body:"Clients can't book you until you list at least one service.",
    action:"Add service", onClick:onOpenAddService, variant:"green", icon:<Sparkles size={14} strokeWidth={2}/>,
  });
  if (!pro.bio || pro.bio.length < 80) suggestions.push({
    title:"Write a fuller bio",
    body:"A longer, specific bio builds trust with new clients.",
    action:"Edit bio", onClick:onOpenBio, variant:"blue", icon:<Users size={14} strokeWidth={2}/>,
  });
  if (!pro.background_checked) suggestions.push({
    title:"Complete a background check",
    body:"Verified pros are shown higher in search results.",
    action:"Get verified", onClick:onOpenVerification, variant:"green", icon:<ShieldCheck size={14} strokeWidth={2}/>,
  });
  if (!pro.payout_method) suggestions.push({
    title:"Set up a payout method",
    body:"Add M-Pesa or bank details so you can actually get paid after a job.",
    action:"Set up payout", onClick:onOpenPayout, variant:"amber", icon:<Wallet size={14} strokeWidth={2}/>,
  });
  if (areasCount === 0) suggestions.push({
    title:"Add the areas you serve",
    body:"Clients searching by neighbourhood won't find you without this.",
    action:"Add areas", onClick:onOpenAvailability, variant:"blue", icon:<MapPin size={14} strokeWidth={2}/>,
  });
  if (scheduleCount === 0) suggestions.push({
    title:"Set your weekly availability",
    body:"A clear schedule reduces back-and-forth on booking requests.",
    action:"Set schedule", onClick:onOpenAvailability, variant:"green", icon:<Clock size={14} strokeWidth={2}/>,
  });
  if (!pro.jirani_name) suggestions.push({
    title:"Pick your Jirani Name",
    body:"This is the friendly name shown on your dashboard and to clients.",
    action:"Set Jirani Name", onClick:onOpenJiraniName, variant:"blue", icon:<Sparkles size={14} strokeWidth={2}/>,
  });

  const styles = {
    amber:{ bg:T.amberTint, bd:T.amberBd, tc:T.amber },
    green:{ bg:T.greenTint, bd:T.greenBd, tc:T.green },
    blue:{ bg:T.blueTint, bd:T.blueBd, tc:T.blue },
  };

  return (
    <Section eyebrow="Keep growing" title="Grow your profile">
      {suggestions.length === 0 ? (
        <div style={{ textAlign:"center", padding:"14px 0 4px" }}>
          <Award size={22} color={T.greenBd} strokeWidth={1.6} style={{marginBottom:8}} />
          <p style={{ fontSize:13, color:T.faint, fontFamily:T.sans, margin:0 }}>Your profile is in great shape. No growth actions right now.</p>
        </div>
      ) : (
        <div style={{ display:"flex", flexDirection:"column", gap:9 }}>
          {suggestions.map((s,i) => {
            const st = styles[s.variant];
            return (
              <div key={i} className="pd-sugg-card" style={{ background:st.bg, borderLeft:`3px solid ${st.tc}` }}>
                <div style={{ display:"flex", alignItems:"flex-start", justifyContent:"space-between", gap:12 }}>
                  <div style={{ display:"flex", gap:10 }}>
                    <span style={{ color:st.tc, flexShrink:0, marginTop:2 }}>{s.icon}</span>
                    <div>
                      <p style={{ fontSize:13, fontWeight:700, color:T.text, margin:"0 0 3px", fontFamily:T.sans }}>{s.title}</p>
                      <p style={{ fontSize:12, color:T.sub, margin:0, fontFamily:T.sans, lineHeight:1.5 }}>{s.body}</p>
                    </div>
                  </div>
                  <Btn v="ghost" size="sm" onClick={s.onClick}>{s.action}</Btn>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </Section>
  );
}

function PeerCard({ item }) {
  const navigate = useNavigate();
  return (
    <div className="pd-peer-card" onClick={()=>navigate(`/pro/${item.id}`)}>
      <div style={{ position:"relative", width:"100%", aspectRatio:"1 / 1", background:T.borderFaint }}>
        {item.img ? (
          <img src={item.img} alt={item.proName} style={{ width:"100%", height:"100%", objectFit:"cover" }} />
        ) : (
          <div style={{ width:"100%", height:"100%", display:"flex", alignItems:"center", justifyContent:"center", background:avatarColor(item.proName) }}>
            <span style={{ color:"#fff", fontSize:24, fontWeight:700, fontFamily:T.sans }}>{toInitials(item.proName)}</span>
          </div>
        )}
      </div>
      <div style={{ padding:"9px 10px 11px" }}>
        <p style={{ margin:"0 0 2px", fontSize:12.5, fontWeight:700, color:T.text, fontFamily:T.sans, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{item.proName}</p>
        <p style={{ margin:"0 0 4px", fontSize:13, fontWeight:800, color:T.text, fontFamily:T.sans, letterSpacing:"-0.02em" }}>{fmtKsh(item.price)}</p>
        {item.rating != null && (
          <div style={{ display:"flex", alignItems:"center", gap:3 }}>
            <Star size={11} fill={T.gold} color={T.gold} />
            <span style={{ fontSize:10.5, fontWeight:700, color:T.text, fontFamily:T.sans }}>{item.rating.toFixed(1)}</span>
            <span style={{ fontSize:10, color:T.faint, fontFamily:T.sans }}>({item.reviews})</span>
          </div>
        )}
      </div>
    </div>
  );
}

function PeerRow({ peers }) {
  if (!peers.length) return null;
  return (
    <Section eyebrow="Benchmark yourself" title="Pros like you" sub="Real listings from other pros in your category — see how your pricing and ratings compare">
      <div className="jp-scroll" style={{ display:"flex", gap:10, overflowX:"auto", paddingBottom:2 }}>
        {peers.map(p => <PeerCard key={p.id} item={p}/>)}
      </div>
    </Section>
  );
}

/* ─────────────────────────────────────
   NEW: ProAccordionSection (mobile accordions)
───────────────────────────────────── */
function ProAccordionAction({ icon: IconComp, label, onClick }) {
  return (
    <button
      onClick={onClick}
      className="pd-accordion-row"
      style={{
        width: "100%", display: "flex", alignItems: "center", gap: 12,
        padding: "14px 16px", background: T.white, border: `1.5px solid ${T.border}`,
        borderRadius: T.r16, boxShadow: T.s1, marginBottom: 10, cursor: "pointer",
        textAlign: "left", transition: T.tx,
      }}
    >
      <span style={{
        width: 32, height: 32, borderRadius: T.r10, flexShrink: 0,
        background: T.card2, border: `1px solid ${T.border}`,
        display: "flex", alignItems: "center", justifyContent: "center",
      }}>
        <IconComp size={15} strokeWidth={1.8} color={T.sub} />
      </span>
      <span style={{ flex: 1, fontSize: 13.5, fontWeight: 500, color: T.text, fontFamily: T.sans }}>{label}</span>
      <ChevronRight size={15} color={T.faint} strokeWidth={2.2} />
    </button>
  );
}

function ProAccordionSection({ icon: IconComp, label, open, onToggle, children }) {
  return (
    <div style={{ background: T.white, border: `1.5px solid ${T.border}`, borderRadius: T.r16, overflow: "hidden", boxShadow: T.s1, marginBottom: 10 }}>
      <button onClick={onToggle} className="pd-accordion-row" style={{ width: "100%", display: "flex", alignItems: "center", gap: 12, padding: "14px 16px", background: "none", border: "none", cursor: "pointer", textAlign: "left", transition: T.tx }}>
        <span style={{ width: 32, height: 32, borderRadius: T.r10, flexShrink: 0, background: open ? T.greenTint : T.card2, border: `1px solid ${open ? T.greenBd : T.border}`, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <IconComp size={15} strokeWidth={1.8} color={open ? T.greenDark : T.sub} />
        </span>
        <span style={{ flex: 1, fontSize: 13.5, fontWeight: open ? 700 : 500, color: T.text, fontFamily: T.sans }}>{label}</span>
        <span style={{ transition: T.tx, transform: open ? "rotate(180deg)" : "rotate(0deg)", display: "flex" }}>
          <ChevronDown size={15} color={T.faint} strokeWidth={2.2} />
        </span>
      </button>
      <div style={{ maxHeight: open ? 6000 : 0, overflow: "hidden", transition: "max-height 0.32s cubic-bezier(.4,0,.2,1)" }}>
        <div style={{ padding: "4px 16px 20px" }}>{children}</div>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────
   NEW: ProPromoBanners (desktop + mobile pills)
───────────────────────────────────── */
function ProPromoBanners({ navigate }) {
  const { isMobile } = useBreakpoint();

  if (isMobile) {
    return (
      <div style={{ display: "flex", gap: 8, marginTop: 20, marginBottom: 14 }}>
        <button onClick={() => navigate("/pro-perks")} style={{
          display: "flex", alignItems: "center", gap: 8,
          padding: "12px", borderRadius: T.r14, border: "none", cursor: "pointer",
          background: T.greenTint, flex: "1 1 0", minWidth: 0, textAlign: "left",
        }}>
          <Gift size={16} color={T.green} strokeWidth={2} style={{ flexShrink: 0 }} />
          <span style={{ fontSize: 12, fontWeight: 700, color: T.greenDark, fontFamily: T.sans, lineHeight: 1.3, whiteSpace: "normal" }}>
            Pro perks &amp; discounts
          </span>
        </button>

        <button onClick={() => navigate("/pro-referrals")} style={{
          display: "flex", alignItems: "center", gap: 8,
          padding: "12px", borderRadius: T.r14, border: "none", cursor: "pointer",
          background: T.greenDeep, flex: "1 1 0", minWidth: 0, textAlign: "left",
        }}>
          <span style={{ width: 24, height: 24, borderRadius: "50%", background: "rgba(255,255,255,0.16)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
            <Users size={12} color="#fff" strokeWidth={2} />
          </span>
          <span style={{ fontSize: 12, fontWeight: 700, color: "#fff", fontFamily: T.sans, lineHeight: 1.3, whiteSpace: "normal" }}>
            Invite, earn up to KSh 25,000
          </span>
        </button>
      </div>
    );
  }

  return (
    <div style={{ display: "flex", gap: 14, marginTop: 28, marginBottom: 20 }}>
      <button onClick={() => navigate("/pro-perks")} className="pd-promo-banner" style={{
        flex: 1, display: "flex", alignItems: "center", gap: 14, padding: "18px 22px",
        borderRadius: T.r16, border: `1.5px solid ${T.greenBd}`, cursor: "pointer", textAlign: "left",
        background: `linear-gradient(135deg, ${T.greenTint} 0%, #e2f4ea 100%)`,
        boxShadow: T.s1, position: "relative", overflow: "hidden",
      }}>
        <span style={{ width: 40, height: 40, borderRadius: T.r12, background: T.white, border: `1px solid ${T.greenBd}`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
          <Gift size={18} color={T.green} strokeWidth={1.9} />
        </span>
        <div style={{ minWidth: 0 }}>
          <p style={{ margin: 0, fontSize: 14.5, fontWeight: 800, color: T.greenDark, fontFamily: T.sans }}>Pro perks &amp; discounts</p>
          <p style={{ margin: "2px 0 0", fontSize: 12, color: T.greenDark, opacity: 0.75, fontFamily: T.sans }}>Deals on tools, supplies, and insurance</p>
        </div>
      </button>

      <button onClick={() => navigate("/pro-referrals")} className="pd-promo-banner" style={{
        flex: 1, display: "flex", alignItems: "center", gap: 14, padding: "18px 22px",
        borderRadius: T.r16, border: "none", cursor: "pointer", textAlign: "left",
        background: `linear-gradient(135deg, ${T.greenDeep} 0%, #0f3a22 55%, ${T.greenDark} 100%)`,
        boxShadow: "0 8px 24px rgba(13,46,26,0.28)", position: "relative", overflow: "hidden",
      }}>
        <div style={{ position: "absolute", top: -40, right: -30, width: 130, height: 130, borderRadius: "50%", background: "radial-gradient(circle, rgba(127,227,172,0.16), transparent 70%)", pointerEvents: "none" }} />
        <span style={{ width: 40, height: 40, borderRadius: T.r12, background: "rgba(255,255,255,0.14)", border: "1px solid rgba(255,255,255,0.22)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, position: "relative" }}>
          <Users size={18} color="#fff" strokeWidth={1.9} />
        </span>
        <div style={{ minWidth: 0, position: "relative" }}>
          <p style={{ margin: 0, fontSize: 14.5, fontWeight: 800, color: "#fff", fontFamily: T.sans }}>Invite a Pro, earn up to KSh 25,000</p>
          <p style={{ margin: "2px 0 0", fontSize: 12, color: "rgba(255,255,255,0.68)", fontFamily: T.sans }}>Get paid when they complete their first job</p>
        </div>
      </button>
    </div>
  );
}

/* ─────────────────────────────────────
   NEW: RecentJobsRail
───────────────────────────────────── */
function RecentJobsRail({ paidJobs, onOpenBookings, onShareProfile }) {
  const { isMobile } = useBreakpoint();
  const recent = useMemo(
    () => [...paidJobs].sort((a, b) => new Date(b.scheduled_time || b.created_at) - new Date(a.scheduled_time || a.created_at)).slice(0, 10),
    [paidJobs]
  );
  const cardW = isMobile ? 148 : 200;

  return (
    <section style={{ marginTop: isMobile ? 28 : 44 }}>
      <div style={{ display: "flex", alignItems: "flex-end", justifyContent: "space-between", gap: 16, marginBottom: 16, flexWrap: "wrap" }}>
        <div>
          <p style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.09em", textTransform: "uppercase", color: T.green, fontFamily: T.sans, margin: "0 0 6px" }}>Recently completed</p>
          <h2 style={{ fontFamily: T.serif, fontSize: isMobile ? 18 : 22, fontWeight: 400, fontStyle: "italic", color: T.text, margin: 0, letterSpacing: "-0.01em" }}>Your recent jobs</h2>
        </div>
        <button onClick={onOpenBookings} style={{ fontSize: 12, fontWeight: 700, color: T.green, background: "none", border: "none", cursor: "pointer", fontFamily: T.sans, display: "flex", alignItems: "center", gap: 4 }}>
          See all <ArrowUpRight size={13} strokeWidth={2.4}/>
        </button>
      </div>
      {recent.length === 0 ? (
        <div style={{ textAlign:"center", padding:"26px 16px", border:`1.5px solid ${T.border}`, borderRadius:T.r14 }}>
          <div style={{ width:36, height:36, borderRadius:"50%", background:T.greenTint, display:"flex", alignItems:"center", justifyContent:"center", margin:"0 auto 10px" }}>
            <Briefcase size={17} color={T.green} strokeWidth={1.8} />
          </div>
          <p style={{ fontSize:12.5, color:T.sub, fontFamily:T.sans, margin:"0 0 10px" }}>Jobs will show up here once a client pays.</p>
          <button onClick={onShareProfile} style={{ background:"none", border:"none", cursor:"pointer", fontSize:12, fontWeight:700, color:T.green, fontFamily:T.sans }}>Share your profile</button>
        </div>
      ) : (
        <div className="jp-scroll" style={{ display: "flex", gap: isMobile ? 8 : 12, overflowX: "auto", paddingBottom: 8 }}>
          {recent.map(job => (
            <div key={job.id} onClick={onOpenBookings} style={{
              flex: `0 0 ${cardW}px`, cursor: "pointer", borderRadius: isMobile ? T.r10 : T.r16, overflow: "hidden",
              background: T.white, border: `1px solid ${T.border}`, boxShadow: T.s1, transition: T.tx,
            }}>
              <div style={{ padding: isMobile ? "10px 10px 9px" : "14px 15px 13px" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
                  <Avatar name={job.customer?.full_name || "Client"} url={job.customer?.avatar_url} size={isMobile ? 26 : 30} />
                  <div style={{ minWidth: 0 }}>
                    <p style={{ margin: 0, fontSize: isMobile ? 11 : 12, fontWeight: 700, color: T.text, fontFamily: T.sans, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{job.customer?.full_name || "Client"}</p>
                    <p style={{ margin: 0, fontSize: 10, color: T.faint, fontFamily: T.sans }}>{fmtShort(job.scheduled_time || job.created_at)}</p>
                  </div>
                </div>
                <p style={{ margin: "0 0 6px", fontSize: isMobile ? 11 : 12.5, color: T.sub, fontFamily: T.sans, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{job.description || "Service"}</p>
                <p style={{ margin: 0, fontSize: isMobile ? 12.5 : 14, fontWeight: 800, color: T.greenDark, fontFamily: T.sans }}>{fmtKsh(job.estimated_price)}</p>
              </div>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

/* ─────────────────────────────────────
   NEW: ProFooter
───────────────────────────────────── */
const PRO_FOOTER_COLUMNS = [
  {
    heading: "For Pros",
    links: [
      { label: "Manage Services",      path: "/pro/services" },
      { label: "Bookings & Schedule",  path: "/pro/bookings" },
      { label: "Earnings & Payouts",   path: "/pro/earnings" },
      { label: "Grow My Profile",      path: "/pro/growth" },
      { label: "Pro Perks & Discounts",path: "/pro-perks" },
      { label: "Invite a Pro",         path: "/pro-referrals" },
    ],
  },
  {
    heading: "Resources",
    links: [
      { label: "Community Hub",    path: "/community-hub" },
      { label: "Tips & Resources", path: "/tips" },
      { label: "Events",           path: "/events" },
      { label: "Guides",           path: "/guides" },
      { label: "Help & Support",   path: "/help" },
    ],
  },
  {
    heading: "Company",
    links: [
      { label: "About JiraniApp",  path: "/about" },
      { label: "Trust & Safety",   path: "/trust-and-safety" },
      { label: "Terms of Service", path: "/terms" },
      { label: "Privacy Policy",   path: "/privacy" },
    ],
  },
];
const PRO_FOOTER_SOCIALS = [
  { name: "TikTok", url: "https://www.tiktok.com/@jiraniapp", d: "M9 3v10.55A4 4 0 1 0 13 17V7h4a4 4 0 0 1-4-4z" },
  { name: "Instagram", url: "https://www.instagram.com/jiraniapp", d: "M12 2.163c3.204 0 3.584.012 4.85.07 1.366.062 2.633.334 3.608 1.308.975.975 1.246 2.242 1.308 3.608.058 1.265.07 1.645.07 4.85s-.012 3.584-.07 4.85c-.062 1.366-.334 2.633-1.308 3.608-.975.975-2.242 1.246-3.608 1.308-1.265.058-1.645.07-4.85.07s-3.584-.012-4.85-.07c-1.366-.062-2.633-.334-3.608-1.308-.975-.975-1.246-2.242-1.308-3.608C2.175 15.584 2.163 15.204 2.163 12s.012-3.584.07-4.85c.062-1.366.334-2.633 1.308-3.608C4.516 2.497 5.783 2.225 7.15 2.233 8.415 2.175 8.795 2.163 12 2.163zm0-2.163C8.741 0 8.332.014 7.052.072 5.197.157 3.355.673 2.014 2.014.673 3.355.157 5.197.072 7.052.014 8.332 0 8.741 0 12c0 3.259.014 3.668.072 4.948.085 1.855.601 3.697 1.942 5.038 1.341 1.341 3.183 1.857 5.038 1.942C8.332 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 1.855-.085 3.697-.601 5.038-1.942 1.341-1.341 1.857-3.183 1.942-5.038.058-1.28.072-1.689.072-4.948 0-3.259-.014-3.668-.072-4.948-.085-1.855-.601-3.697-1.942-5.038C20.645.673 18.803.157 16.948.072 15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 1 0 0 12.324 6.162 6.162 0 0 0 0-12.324zM12 16a4 4 0 1 1 0-8 4 4 0 0 1 0 8zm6.406-11.845a1.44 1.44 0 1 0 0 2.881 1.44 1.44 0 0 0 0-2.881z" },
  { name: "X", url: "https://twitter.com/jiraniapp", d: "M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-4.714-6.231-5.401 6.231H2.747l7.73-8.835L1.254 2.25H8.08l4.261 5.635 5.903-5.635zm-1.161 17.52h1.833L7.084 4.126H5.117z" },
  { name: "Facebook", url: "https://www.facebook.com/jiraniapp", d: "M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" },
];
const PRO_FOOTER_ADDRESS = "Delta Corner, Chiromo Road, Westlands, Nairobi";

function ProMobileFooterSection({ heading, links, open, onToggle, navigate, dark }) {
  return (
    <div style={{ borderBottom: `1px solid ${dark.border}` }}>
      <button onClick={onToggle} style={{ width: "100%", display: "flex", alignItems: "center", justifyContent: "space-between", padding: "14px 0", background: "none", border: "none", cursor: "pointer", fontFamily: T.sans }}>
        <span style={{ fontSize: 10, fontWeight: 700, color: dark.onDark, letterSpacing: ".03em", textTransform: "uppercase" }}>{heading}</span>
        <ChevronDown size={14} color={dark.onDarkSub} strokeWidth={2.2} style={{ transform: open ? "rotate(180deg)" : "rotate(0deg)", transition: T.tx }} />
      </button>
      <div style={{ maxHeight: open ? 600 : 0, overflow: "hidden", transition: "max-height 0.28s cubic-bezier(.4,0,.2,1)" }}>
        <ul style={{ listStyle: "none", margin: 0, padding: "0 0 14px" }}>
          {links.map((link, j) => (
            <li key={j} style={{ marginBottom: 7 }}>
              <span onClick={() => navigate(link.path)} className="pd-foot-link" style={{ fontSize: 12, color: dark.onDarkSub, cursor: "pointer", lineHeight: 1.4, fontFamily: T.sans, transition: T.tx, display: "inline-block" }}>{link.label}</span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

function ProFooter({ navigate }) {
  const { isMobile } = useBreakpoint();
  const dark = { bg: "#0d2e1a", border: "#1e4a2e", onDark: "#FFFFFF", onDarkSub: "rgba(255,255,255,0.55)", onDarkFaint: "rgba(255,255,255,0.28)" };
  const [openSections, setOpenSections] = useState({});
  const toggle = i => setOpenSections(prev => ({ ...prev, [i]: !prev[i] }));

  return (
    <footer style={{ background: dark.bg, fontFamily: T.sans, width: "100%", marginTop: 40, position: "relative" }}>
      <div style={{ maxWidth: 1200, margin: "0 auto", padding: isMobile ? "8px 20px 0" : "36px 40px 8px" }}>
        {isMobile ? (
          <div style={{ marginBottom: 8 }}>
            {PRO_FOOTER_COLUMNS.map((col, i) => (
              <ProMobileFooterSection key={i} heading={col.heading} links={col.links} open={!!openSections[i]} onToggle={() => toggle(i)} navigate={navigate} dark={dark} />
            ))}
          </div>
        ) : (
          <div style={{ display: "grid", gridTemplateColumns: "repeat(3, minmax(140px, 1fr))", gap: "0 40px", marginBottom: 32 }}>
            {PRO_FOOTER_COLUMNS.map((col, i) => (
              <div key={i}>
                <p style={{ fontSize: 13, fontWeight: 800, color: dark.onDark, textTransform: "uppercase", letterSpacing: ".06em", margin: "0 0 14px", fontFamily: T.sans }}>{col.heading}</p>
                <ul style={{ listStyle: "none", margin: 0, padding: 0 }}>
                  {col.links.map((link, j) => (
                    <li key={j} style={{ marginBottom: 9 }}>
                      <span onClick={() => navigate(link.path)} className="pd-foot-link" style={{ fontSize: 13, color: dark.onDarkSub, cursor: "pointer", lineHeight: 1.4, fontFamily: T.sans, transition: T.tx, display: "inline-block" }}>{link.label}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        )}
      </div>

      <div style={{ borderTop: `1px solid ${dark.border}`, padding: isMobile ? "16px 14px" : "18px 40px" }}>
        <div style={{ maxWidth: 1200, margin: "0 auto", display: "flex", flexDirection: isMobile ? "column" : "row", alignItems: isMobile ? "stretch" : "center", justifyContent: "space-between", gap: isMobile ? 14 : 0 }}>
          <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-start", gap: 3 }}>
            <div onClick={() => navigate("/")} style={{ cursor: "pointer" }}>
              <AppLogo variant="light" size="sm" />
            </div>
            <span style={{ fontFamily: T.serif, fontStyle: "italic", fontSize: 11.5, color: dark.onDarkSub }}>Right next door</span>
          </div>

          {!isMobile && (
            <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
              {PRO_FOOTER_SOCIALS.map((s, i) => (
                <a key={i} href={s.url} target="_blank" rel="noopener noreferrer" title={s.name} style={{ color: dark.onDarkFaint, display: "inline-flex", alignItems: "center", transition: T.tx }}>
                  <svg viewBox="0 0 24 24" width={15} height={15} fill="currentColor"><path d={s.d} /></svg>
                </a>
              ))}
            </div>
          )}

          <div style={{ display: "flex", flexDirection: "column", alignItems: isMobile ? "flex-start" : "flex-end", gap: 2 }}>
            <span style={{ fontSize: isMobile ? 10 : 11.5, color: dark.onDarkFaint, fontFamily: T.sans }}>© JiraniApp Ltd. 2025</span>
            <span style={{ fontSize: isMobile ? 9.5 : 11.5, color: dark.onDarkFaint, fontFamily: T.sans }}>{PRO_FOOTER_ADDRESS}</span>
          </div>
        </div>
      </div>
    </footer>
  );
}

/* ─────────────────────────────────────
   PAGE SKELETON
───────────────────────────────────── */
function Skel({ w="100%", h=16, r=8 }) {
  return <div style={{width:w,height:h,borderRadius:r,background:"#edeef0",animation:"pdPulse 1.5s ease-in-out infinite"}}/>;
}
function PageSkeleton() {
  const { isMobile } = useBreakpoint();
  return (
    <div style={{ display:"flex" }}>
      {!isMobile && <div style={{ width:250, flexShrink:0 }} />}
      <div style={{flex:1, padding: isMobile ? "16px" : "28px 28px"}}>
        <Skel h={140} r={16}/>
        <div style={{display:"grid",gridTemplateColumns: isMobile ? "1fr" : "2fr 1fr",gap:20,marginTop:20}}>
          <div style={{display:"flex",flexDirection:"column",gap:16}}>
            {[170,190,230].map((h,i)=><Skel key={i} h={h} r={16}/>)}
          </div>
          <div style={{display:"flex",flexDirection:"column",gap:16}}>
            {[150,190,270].map((h,i)=><Skel key={i} h={h} r={16}/>)}
          </div>
        </div>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────
   HIDDEN AVATAR-UPLOAD (triggered from the sidebar avatar) —
   reuses the same pending-approval flow as ProSettingsPanel's
   Account section, so the dashboard is a real place to update
   your photo without opening Settings.
───────────────────────────────────── */
function useAvatarUpload(pro, onToast, onProUpdate) {
  const fileRef = useRef(null);
  const trigger = () => fileRef.current?.click();
  const handleChange = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file || !pro?.id) return;
    if (file.size > 5 * 1024 * 1024) { onToast("Image must be under 5MB", "error"); return; }
    if (!file.type.startsWith("image/")) { onToast("File must be an image", "error"); return; }
    try {
      const path = `pro_${pro.id}/pending-${Date.now()}.jpg`;
      const { data, error } = await supabase.storage.from("avatars").upload(path, file, { upsert: true });
      if (error) throw error;
      const { data: { publicUrl } } = supabase.storage.from("avatars").getPublicUrl(data.path);
      const { error: updErr } = await supabase.from("profiles").update({
        pending_avatar_url: publicUrl, avatar_status: "pending", avatar_rejection_reason: null,
      }).eq("id", pro.id);
      if (updErr) throw updErr;
      onProUpdate({ pending_avatar_url: publicUrl, avatar_status: "pending" });
      try {
        await supabase.from("notifications").insert({
          type: "pro_review", subtype: "photo_pending",
          title: "New profile photo awaiting approval",
          body: `${pro.full_name || pro.jirani_name || "A pro"} uploaded a new profile photo.`,
          link_to: `/admin/pros/${pro.id}?tab=profile`, recipient_id: null, read: false, status: "unread",
        });
      } catch { /* best-effort */ }
      onToast("Photo submitted — pending admin approval", "success");
    } catch (err) {
      onToast(err.message || "Couldn't upload your photo.", "error");
    }
  };
  const Input = () => <input ref={fileRef} type="file" accept="image/*" style={{ display:"none" }} onChange={handleChange} />;
  return { trigger, Input };
}

/* ─────────────────────────────────────
   PAGE ROOT
───────────────────────────────────── */
export default function ProDashboardHome() {
  const { user, isLoggedIn, isPro, logoutUser } = useAuth();
  const navigate = useNavigate();
  const { isMobile } = useBreakpoint();

  const [loading, setLoading]           = useState(true);
  const [pro, setPro]                   = useState(null);
  const [services, setServices]         = useState([]);
  const [bookings, setBookings]         = useState([]);
  const [reviews, setReviews]           = useState([]);
  const [portfolio, setPortfolio]       = useState([]);
  const [skills, setSkills]             = useState([]);
  const [areas, setAreas]               = useState([]);
  const [schedule, setSchedule]         = useState([]);
  const [peers, setPeers]               = useState([]);
  const [unreadMessages, setUnreadMessages] = useState(0);
  const [unreadNotifs, setUnreadNotifs] = useState(0);
  const [error, setError]               = useState(null);
  const [activeTab, setActiveTab]       = useState("overview");
  const [toasts, setToasts]             = useState([]);
  const [openMobileSection, setOpenMobileSection] = useState("overview");

  const [showAddService, setShowAddService] = useState(false);
  const [editingService, setEditingService] = useState(null);
  const [showAvailability, setShowAvailability] = useState(false);
  const [showBookings, setShowBookings] = useState(false);
  const [showPortfolioMedia, setShowPortfolioMedia] = useState(false);
  const [showBioEdit, setShowBioEdit] = useState(false);
  const [showVerification, setShowVerification] = useState(false);
  const [showPayoutSetup, setShowPayoutSetup] = useState(false);
  const [showJiraniName, setShowJiraniName] = useState(false);

  const toast = useCallback((message, type = "info") => {
    const id = Date.now() + Math.random();
    setToasts(t => [...t, { id, message, type }]);
    setTimeout(() => setToasts(t => t.filter(x => x.id !== id)), 3500);
  }, []);

  useEffect(() => {
    if (!isLoggedIn || !isPro) { navigate('/'); }
  }, [isLoggedIn, isPro, navigate]);

  const fetchDashboard = useCallback(async () => {
    if (!user?.id) return;
    setLoading(true); setError(null);
    try {
      const { data: proProfile, error: proErr } = await supabase
        .from("pro_profiles")
        .select(`
          id, bio, title, base_price, completed_jobs,
          success_score, response_minutes, repeat_rate,
          availability, background_checked, phone_verified, phone, phone_locked_until,
          member_since, badge, category, review_count, response_rate,
          avg_rating:rating, verified:is_verified,
          jirani_name, pro_number,
          payout_method, payout_mpesa_number, payout_bank_name,
          payout_account_number, payout_account_name,
          profiles!user_id ( full_name, avatar_url, pending_avatar_url, avatar_status, avatar_rejection_reason, location, email )
        `)
        .eq("user_id", user.id)
        .single();
      if (proErr) throw proErr;

      const proId = proProfile.id;

      const [servicesRes, bookingsRes, reviewsRes, portfolioRes, skillsRes, areasRes, scheduleRes, notifCountRes] = await Promise.all([
        supabase.from("pro_services").select("id, name, headline, description, price, duration_minutes, category, status, rejection_reason, included_items, excluded_items, prep_notes, team_size, supplies_included").eq("pro_id", proId).order("created_at", { ascending:false }),
        supabase.from("job_requests").select(`
            id, description, status, scheduled_time, location, notes,
            estimated_price, payment_status, created_at,
            customer:user_id ( id, full_name, avatar_url )
          `).eq("pro_id", proId).order("scheduled_time", { ascending:true }),
        supabase.from("reviews").select(`id, rating, comment, created_at, reviewer:customer_id ( full_name, avatar_url )`).eq("pro_id", proId).order("created_at", { ascending:false }).limit(10),
        supabase.from("portfolio_images").select("id").eq("pro_id", proId),
        supabase.from("pro_skills").select("skill").eq("pro_id", proId),
        supabase.from("pro_service_areas").select("area").eq("pro_id", proId),
        supabase.from("pro_availability").select("day_of_week, start_time, end_time, is_open").eq("pro_id", proId),
        supabase.from("notifications").select("id", { count:"exact", head:true }).eq("recipient_id", user.id).eq("read", false),
      ]);

      let peerRows = [];
      try {
        const { data: peerRaw } = await supabase
          .from("pro_profiles")
          .select(`
            id, base_price, review_count,
            avg_rating:rating,
            profiles!user_id ( full_name ),
            portfolio_images ( url )
          `)
          .eq("category", proProfile.category || "cleaning")
          .neq("id", proId)
          .gte("rating", 4.0)
          .order("rating", { ascending:false })
          .limit(10);
        peerRows = (peerRaw || [])
          .filter(p => p.base_price != null)
          .map(p => ({
            id: p.id,
            proName: p.profiles?.full_name || "JiraniPro",
            price: p.base_price,
            rating: p.avg_rating || null,
            reviews: p.review_count || 0,
            img: p.portfolio_images?.[0]?.url || null,
          }));
      } catch { /* peers are a nice-to-have; ignore failures */ }

      let unreadMsgCount = 0;
      try {
        const { data: cnt } = await supabase.rpc("jirani_unread_message_count", { p_user_id: user.id });
        unreadMsgCount = cnt || 0;
      } catch { /* messages RPC not confirmed yet — fails open to 0 rather than crashing the dashboard */ }

      setPro({
        ...proProfile,
        full_name: proProfile.profiles?.full_name || "Your name",
        avatar_url: proProfile.profiles?.avatar_url || null,
        pending_avatar_url: proProfile.profiles?.pending_avatar_url || null,
        avatar_status: proProfile.profiles?.avatar_status || "approved",
        avatar_rejection_reason: proProfile.profiles?.avatar_rejection_reason || null,
        city: proProfile.profiles?.location || null,
        email: proProfile.profiles?.email || null,
      });
      setServices(servicesRes.data || []);
      setBookings(bookingsRes.data || []);
      setReviews(reviewsRes.data || []);
      setPortfolio(portfolioRes.data || []);
      setSkills((skillsRes.data || []).map(r => r.skill).filter(Boolean));
      setAreas((areasRes.data || []).map(r => r.area).filter(Boolean));
      setSchedule(scheduleRes.data || []);
      setPeers(peerRows);
      setUnreadMessages(unreadMsgCount);
      setUnreadNotifs(notifCountRes.count || 0);
    } catch (e) {
      console.error("Dashboard fetch failed:", e);
      setError("We couldn't load your dashboard. Please refresh, or try again shortly.");
    } finally {
      setLoading(false);
    }
  }, [user?.id]);

  useEffect(() => { fetchDashboard(); }, [fetchDashboard]);

  // Realtime: availability changes made anywhere (this dashboard, Settings,
  // or an admin override) reflect here instantly without a refetch.
  useEffect(() => {
    if (!pro?.id) return;
    const channel = supabase
      .channel(`pro_profile_${pro.id}`)
      .on("postgres_changes", { event: "UPDATE", schema: "public", table: "pro_profiles", filter: `id=eq.${pro.id}` }, (payload) => {
        setPro(p => p ? { ...p, ...payload.new } : p);
      })
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, [pro?.id]);

  const handleProUpdate = useCallback((patch) => setPro(p => p ? { ...p, ...patch } : p), []);

  const avatarUpload = useAvatarUpload(pro, toast, handleProUpdate);

  const handleMessage = useCallback(async (customerUserId) => {
    if (!customerUserId) { navigate("/messages"); return; }
    try {
      const { data: ex } = await supabase.from("conversation_participants").select("conversation_id").eq("user_id", user.id);
      const mine = new Set((ex||[]).map(r=>r.conversation_id));
      const { data: theirs } = await supabase.from("conversation_participants").select("conversation_id").eq("user_id", customerUserId);
      const shared = (theirs||[]).find(r => mine.has(r.conversation_id));
      if (shared) { navigate(`/messages/${shared.conversation_id}`); return; }
      navigate("/messages");
    } catch {
      navigate("/messages");
    }
  }, [user?.id, navigate]);

  const now = new Date();
  const upcomingJobs = useMemo(() =>
    bookings.filter(b => ["pending","confirmed"].includes(b.status) && new Date(b.scheduled_time) >= now),
  [bookings]);
  const nextJob = upcomingJobs[0] || null;
  const paidJobs = useMemo(() => bookings.filter(b => b.payment_status === "paid"), [bookings]);

  const completionPct = useMemo(() => {
    if (!pro) return 0;
    let score = 0;
    if (pro.avatar_url && pro.avatar_status !== "rejected") score += 8;
    if (pro.jirani_name) score += 6;
    if (pro.bio && pro.bio.length > 80) score += 12;
    if (pro.title) score += 4;
    const approvedServices = services.filter(s => s.status === "approved");
    score += Math.min(1, approvedServices.length / 1) * 16;
    score += Math.min(1, portfolio.length/5) * 12;
    if (portfolio.some(p => p.is_main)) score += 4;
    if (pro.verified) score += 6;
    if (pro.phone_verified) score += 6;
    if (pro.payout_method) score += 10;
    if (areas.length > 0) score += 8;
    if (schedule.some(s => s.is_open)) score += 8;
    return Math.min(100, Math.round(score));
  }, [pro, services, portfolio, areas, schedule]);

  const { trustLabel, trustColor } = useMemo(() => {
    if (!pro) return { trustLabel:"New pro", trustColor:"neutral" };
    if (pro.badge) return { trustLabel: pro.badge, trustColor:"gold" };
    if ((pro.avg_rating||0) >= 4.7 && (pro.completed_jobs||0) >= 20) return { trustLabel:"Top rated", trustColor:"gold" };
    if (pro.verified) return { trustLabel:"Verified pro", trustColor:"green" };
    return { trustLabel:"New pro", trustColor:"neutral" };
  }, [pro]);

  const activityEvents = useMemo(() => {
    const events = [];
    bookings.forEach(b => {
      events.push({
        id:`booking_${b.id}`, type:"booking",
        title: b.payment_status === "paid"
          ? `Payment received – ${b.description || "Service"}`
          : `Booking ${b.status === "confirmed" ? "confirmed" : "requested"} – ${b.description || "Service"}`,
        sub: b.customer?.full_name || "Client",
        stars: null, ts: b.created_at,
      });
    });
    reviews.forEach(r => {
      events.push({
        id:`review_${r.id}`, type:"review",
        title: `New ${r.rating}-star review – ${r.reviewer?.full_name || "Client"}`,
        sub: r.comment ? (r.comment.length > 70 ? r.comment.slice(0,70)+"…" : r.comment) : "",
        stars: r.rating, ts: r.created_at,
      });
    });
    return events.sort((a,b) => new Date(b.ts) - new Date(a.ts));
  }, [bookings, reviews]);

  const tabs = [
    { id:"overview", label:"Overview", Icon: Home },
    { id:"earnings", label:"Earnings", Icon: Wallet },
    { id:"bookings", label:"Bookings", Icon: Calendar },
    { id:"growth",   label:"Growth",   Icon: Award },
  ];

  const gridColumns = isMobile ? "1fr" : "2fr 1fr";

  const overviewContent = (
    <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : gridColumns, gap: 20, alignItems: "start" }}>
      <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
        <TodaysFocus nextJob={nextJob} onMessage={() => handleMessage(nextJob?.customer?.id)} onViewDetails={() => setShowBookings(true)} onOpenAvailability={() => setShowAvailability(true)} />
        <ServicesSummary services={services} onAddService={() => { setEditingService(null); setShowAddService(true); }} onEditService={(s) => { setEditingService(s); setShowAddService(true); }} />
        <ActivityFeed events={activityEvents} onOpenBookings={() => setShowBookings(true)} />
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
        <PerformanceSnapshot pro={pro} />
        <TrustSection pro={pro} onManage={() => setShowVerification(true)} />
        <SkillsSection skills={skills} />
      </div>
    </div>
  );

  const earningsContent = (
    <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : gridColumns, gap: 20, alignItems: "start" }}>
      <EarningsSection paidJobs={paidJobs} />
      <PayoutSection pro={pro} paidJobs={paidJobs} onManage={() => setShowPayoutSetup(true)} />
    </div>
  );

  const bookingsContent = (
    <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : gridColumns, gap: 20, alignItems: "start" }}>
      <UpcomingJobs jobs={upcomingJobs} onOpenBookings={() => setShowBookings(true)} />
      <AreasAvailabilitySection areas={areas} schedule={schedule} onEdit={() => setShowAvailability(true)} />
    </div>
  );

  const growthContent = (
    <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
      <GrowthSuggestions
        pro={pro} services={services} portfolioCount={portfolio.length} areasCount={areas.length} scheduleCount={schedule.filter(s => s.is_open).length}
        onOpenPhotos={() => setShowPortfolioMedia(true)}
        onOpenAddService={() => { setEditingService(null); setShowAddService(true); }}
        onOpenBio={() => setShowBioEdit(true)}
        onOpenVerification={() => setShowVerification(true)}
        onOpenPayout={() => setShowPayoutSetup(true)}
        onOpenAvailability={() => setShowAvailability(true)}
        onOpenJiraniName={() => setShowJiraniName(true)}
      />
      <PeerRow peers={peers} />
    </div>
  );

  if (loading) {
    return (
      <div style={{ minHeight:"100vh", background:T.bg, fontFamily:T.sans }}>
        <style>{CSS}</style>
        <PageSkeleton/>
      </div>
    );
  }

  if (error || !pro) {
    return (
      <div style={{ minHeight:"100vh", background:T.bg, fontFamily:T.sans }}>
        <style>{CSS}</style>
        <div style={{ maxWidth:480, margin:"90px auto", textAlign:"center", padding:"0 20px" }}>
          <AlertCircle size={28} color={T.ghost} strokeWidth={1.6} style={{marginBottom:14}} />
          <p style={{ fontSize:15, color:T.textMid, fontFamily:T.sans, marginBottom:18, lineHeight:1.6 }}>
            {error || "We couldn't find a professional profile for this account."}
          </p>
          <Btn v="primary" onClick={fetchDashboard}>Try again</Btn>
        </div>
      </div>
    );
  }

  const shareProfile = () => {
    navigator.clipboard.writeText(`${window.location.origin}/pro/${pro.id}`);
    toast("Profile link copied.", "success");
  };

  return (
    <div style={{ minHeight:"100vh", background:T.bg, fontFamily:T.sans, display:"flex" }}>
      <style>{CSS}</style>
      <avatarUpload.Input />

      {/* Toasts */}
      <div style={{ position:"fixed", top:16, right:16, zIndex:900, display:"flex", flexDirection:"column", gap:8 }}>
        {toasts.map(t => (
          <div key={t.id} style={{
            background:T.white, border:`1px solid ${t.type==="error"?T.redBd:t.type==="success"?T.greenBd:T.border}`,
            boxShadow:T.s3, borderRadius:T.r12, padding:"12px 16px", display:"flex", alignItems:"center", gap:10,
            minWidth:260, maxWidth:340, animation:"jpPopIn 0.2s ease-out",
          }}>
            {t.type==="success" && <CheckCircle2 size={16} color={T.green} strokeWidth={1.75}/>}
            {t.type==="error" && <XCircle size={16} color={T.red} strokeWidth={1.75}/>}
            {t.type==="info" && <AlertCircle size={16} color={T.blue} strokeWidth={1.75}/>}
            <span style={{ fontSize:13, color:T.textMid, fontFamily:T.sans, lineHeight:1.4 }}>{t.message}</span>
          </div>
        ))}
      </div>

      <ServiceModal
        open={showAddService}
        editingService={editingService}
        onClose={()=>{ setShowAddService(false); setEditingService(null); }}
        proId={pro.id}
        defaultCategory={pro.category}
        onSaved={(row)=> setServices(s => editingService ? s.map(x=>x.id===row.id?row:x) : [row, ...s])}
        onToast={toast}
      />
      <AreasAvailabilityModal open={showAvailability} onClose={()=>setShowAvailability(false)} pro={pro} onToast={toast} onProUpdate={handleProUpdate} />
      <BookingsModal open={showBookings} onClose={()=>setShowBookings(false)} bookings={bookings} pro={pro} />
      <MediaModal open={showPortfolioMedia} onClose={()=>setShowPortfolioMedia(false)} proId={pro.id} defaultCategory={pro.category} onToast={toast} />
      <BioEditModal open={showBioEdit} onClose={()=>setShowBioEdit(false)} pro={pro} onToast={toast} onProUpdate={handleProUpdate} />
      <VerificationModal open={showVerification} onClose={()=>setShowVerification(false)} pro={pro} onToast={toast} />
      <PayoutSetupModal open={showPayoutSetup} onClose={()=>setShowPayoutSetup(false)} pro={pro} onToast={toast} onProUpdate={handleProUpdate} />
      <JiraniNameModal open={showJiraniName} onClose={()=>setShowJiraniName(false)} pro={pro} onToast={toast} onProUpdate={handleProUpdate} />

      {!isMobile && (
        <Sidebar
          pro={pro}
          activeTab={activeTab}
          onNavigate={setActiveTab}
          onOpenServices={()=>{ setEditingService(null); setShowAddService(true); }}
          onOpenPortfolio={()=>setShowPortfolioMedia(true)}
          onAvatarClick={avatarUpload.trigger}
        />
      )}

      <div style={{ flex:1, minWidth:0, display:"flex", flexDirection:"column" }}>
        <TopBar
          pro={pro}
          userId={user?.id}
          unreadMessages={unreadMessages}
          unreadNotifs={unreadNotifs}
          onUnreadNotifsChange={(delta)=>setUnreadNotifs(n=>Math.max(0,n+delta))}
          onOpenServices={()=>{ setEditingService(null); setShowAddService(true); }}
          onOpenPortfolio={()=>setShowPortfolioMedia(true)}
          onSignOut={() => { logoutUser?.(); navigate("/"); }}
        />

        <main style={{ maxWidth:1180, width:"100%", margin:"0 auto", padding: isMobile?"20px 16px 40px":"28px 32px 60px" }}>
          <ProfileHeader
            pro={pro}
            completionPct={completionPct}
            trustLabel={trustLabel}
            trustColor={trustColor}
            onFinishSetup={()=>setActiveTab("growth")}
          />

          {!isMobile && <Tabs tabs={tabs} active={activeTab} onChange={setActiveTab} />}

          {isMobile ? (
            <div>
              <ProAccordionSection icon={Home} label="Overview" open={openMobileSection === "overview"} onToggle={() => setOpenMobileSection(o => o === "overview" ? null : "overview")}>
                {overviewContent}
              </ProAccordionSection>
              <ProAccordionSection icon={Wallet} label="Earnings & payouts" open={openMobileSection === "earnings"} onToggle={() => setOpenMobileSection(o => o === "earnings" ? null : "earnings")}>
                {earningsContent}
              </ProAccordionSection>
              <ProAccordionSection icon={Calendar} label="Bookings & schedule" open={openMobileSection === "bookings"} onToggle={() => setOpenMobileSection(o => o === "bookings" ? null : "bookings")}>
                {bookingsContent}
              </ProAccordionSection>
              <ProAccordionAction icon={Camera} label="Photos & videos" onClick={() => setShowPortfolioMedia(true)} />
              <ProAccordionAction icon={Briefcase} label="Manage services" onClick={() => { setEditingService(null); setShowAddService(true); }} />
              <ProAccordionSection icon={Award} label="Grow your profile" open={openMobileSection === "growth"} onToggle={() => setOpenMobileSection(o => o === "growth" ? null : "growth")}>
                {growthContent}
              </ProAccordionSection>
            </div>
          ) : (
            <>
              {activeTab === "overview" && overviewContent}
              {activeTab === "earnings" && earningsContent}
              {activeTab === "bookings" && bookingsContent}
              {activeTab === "growth" && growthContent}
            </>
          )}

          <ProPromoBanners navigate={navigate} />
          <RecentJobsRail paidJobs={paidJobs} onOpenBookings={() => setShowBookings(true)} onShareProfile={shareProfile} />
        </main>

        <ProFooter navigate={navigate} />
      </div>
    </div>
  );
}